// The browser remix room: a focused arrangement editor.
//
// Two modes:
// - Local (/room/local/:seedId): solo editing, no auth, no server.
//   Bootstraps a CanonicalRoom from the seed's arrangement snapshot and applies
//   edits client-side via the merge engine.
// - New (/room/draft/:draftId): a seed made from nothing, a draft in this
//   browser until its first Save makes it a seed (lib/save-root-seed.ts).
// - Connected (/room/:jamId): multiplayer, auth required.
//   Uses useRoomSocket + useRoomReplica + useJamEditor to edit a live room.
//
// The layout matches the Play screen: PadGrid / Timeline / Mixer views with
// a transport at the bottom.

import { renderStatus, warpRenderHash } from "@shared/sync/clip-render.js";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ChevronDown, Keyboard, Loader2, LogOut, Play, Plus, Repeat, Square, Undo2, Redo2, Users, X } from "lucide-react";
import type { SeedApiDetail } from "@shared/api-types.js";
import type { Snapshot } from "@shared/seed-history.js";
import { MAX_SCENES, emptyRoom, type CanonicalRoom } from "@shared/sync/state.js";
import { deleteDraft, loadDraft, mintDraftId, saveDraft, type RoomDraft } from "@/lib/room-drafts";
import { localTakeKeysOf } from "@/lib/recording";
import type { MyClip } from "@shared/api-types.js";
import { displayNameOf } from "@shared/sync/track-id.js";
import { planHydration } from "@shared/sync/hydrate.js";
import { Button } from "@web-shared/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@web-shared/components/ui/tooltip";
import { ArrangementView, type ArrangementMenuTarget, type EditState, type LaneAudioState } from "@web-shared/components/ArrangementView";
import { arrangementMenu } from "@/lib/arrangement-menu";
import { barsBeats, FOUR_FOUR } from "@web-shared/lib/meter";
import { MixerView } from "@web-shared/components/MixerView";
import { clipColor, fromRoom, laneInstruments, type Arrangement, type Lane, type LaneClip } from "@web-shared/lib/arrangement";
import { beatsToSeconds, formatTime } from "@web-shared/lib/audio-engine";
import type { LaneLevel } from "@web-shared/lib/audio-engine";
import {
  moveClips,
  moveClipToNewLane,
  bounceInPlace,
  completeRender,
  requestRender,
  copyClips,
  copyClipToLane,
  setTrackField,
  setTrackInstrument,
  deleteTrack,
  setClipNotes,
  setClipFields,
  scaleClipLoop,
  setTempo as tempoIntent,
  copyClipToSlot,
  copyClipToNewLane,
  nextOrd,
  insertDevice,
  removeDevice,
  setDeviceParam,
} from "@web-shared/lib/edits";
import { NewTrackRow } from "@/components/NewTrackRow";
import { useAudioFileDrop } from "../hooks/useAudioFileDrop";
import { isInstrumentId, type InstrumentId } from "@shared/instruments.js";
import type { WireNote } from "@shared/midi/notes.js";
import { MidiClipEditor } from "@web-shared/components/piano-roll/MidiClipEditor";
import { ghostNotesFor } from "@web-shared/lib/ghost-notes";
import { AddTrackSheet } from "@/components/AddTrackSheet";
import { TempoReading } from "@/components/TempoReading";
import { useTrackCreation } from "../hooks/useTrackCreation";
import { overwrite, trimClip, type LaneSpan } from "@web-shared/lib/live-edits";
import { DeviceLane } from "@web-shared/components/devices/DeviceLane";
import { stockDevice } from "@web-shared/lib/live-device-defaults";
import { usePref } from "@web-shared/hooks/usePref";
import { clipsInSpan, GRID_DENSITY_MAX, GRID_DENSITY_MIN, type TimeSpan } from "@web-shared/lib/time-selection";
import type { Intent } from "@shared/sync/intents.js";
import type { DeviceState, TrackField } from "@shared/sync/state.js";
import {
  clipAuditionSource,
  effectiveTempo,
  midiSources,
  objectFileSpan,
  roomSourcesWithUrls,
  clipSourcesFromStart,
} from "@web-shared/lib/player-sources";
import { encodeWav24 } from "@web-shared/lib/wav";
import {
  consolidatable,
  consolidatedAudioClip,
  consolidatedMidiClip,
  consolidateIntents,
  consolidateTarget,
  planConsolidate,
} from "@web-shared/lib/consolidate";
import { isLooped, objectPositionAt, objectStart } from "@web-shared/lib/clip-loop";
import { ClipEditor } from "@web-shared/components/clip-editor/ClipEditor";
import { lengthLabel } from "@web-shared/components/clip-editor/chrome";
import { objectUrl } from "@web-shared/lib/api";
import type { WaveformSource } from "@web-shared/components/ArrangementView";
import { withGhostSlots } from "@web-shared/lib/ghost-clips";
import { roomClipFacts, variantKey } from "@web-shared/lib/material";
import { buildSceneGrid } from "@web-shared/lib/scene-grid";
import { buildRoomClipLibrary } from "@web-shared/lib/clip-library";
import { useMaterialVariants } from "@/hooks/useMaterialVariants";
import { MATERIAL_SEARCH_ID, MaterialLibrary } from "@/components/MaterialLibrary";
import { useSheetSnap, type SheetSnap } from "@/hooks/useSheetSnap";
import type { MaterialPayload } from "@web-shared/lib/material-item";
import { snap } from "@web-shared/lib/placement";
import {
  SessionPads,
  type PadLaunchState,
  type PadMixerState,
  type PadQuantize,
  type PadWaveformSource,
  type PadEditState,
} from "@/components/SessionPads";
import { usePadOps } from "../hooks/usePadOps";
import { ghostSource } from "@/lib/pad-ops";
import { usePlayback } from "@web-shared/hooks/usePlayback";
import { stopExternalPreviews } from "@web-shared/lib/external-previews";
import { midiStagesFor, type MidiStage } from "@web-shared/lib/midi-effects";
import { soundingLabel } from "@web-shared/lib/sounding-label";
import { webApi } from "@web-shared/lib/web-api";
import { track as trackAnalytics } from "@web-shared/lib/analytics";
import { ApiError } from "@web-shared/lib/api";
import { hashOfPoolKey, poolHashAnyChannel, previewKeyOf } from "@shared/blobs.js";
import { draftRoomPath, roomPath, seedPath } from "@/lib/ctas";
import { cleanTrackName } from "@/lib/track-name";
import { faderDbLabel, gainDbLabel, isAudible, panLabel } from "@shared/mixer.js";
import { cn } from "@web-shared/lib/utils";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useLocalEditor, type LocalEditor } from "../hooks/useLocalEditor";
import { useClipOps } from "../hooks/useClipOps";
import { ClipActionBar } from "../components/ClipActionBar";
import { ShortcutsHelp, type ShortcutGroup } from "@web-shared/components/ShortcutsHelp";
import { useIsCompact, useIsShort } from "@web-shared/hooks/useIsNarrow";
import { useUnsavedGuard } from "../hooks/useUnsavedGuard";
import { PublishRoom } from "../components/PublishRoom";
import { StartJamButton } from "../components/StartJamButton";
import { RecordButton, RecordSettings } from "../components/RecordControls";
import { useTakeStore, type TakeStore } from "../hooks/useTakeStore";
import { useRoomDownloads } from "../hooks/useRoomDownloads";
import { DownloadButton } from "../components/DownloadButton";
import { SaveNewSeed } from "../components/SaveNewSeed";
import { useRoomRecorder } from "../hooks/useRoomRecorder";
import { useMyClips } from "../hooks/useMyClips";
import { usePoolPreviews } from "../hooks/usePoolPreviews";
import { useMineShelf } from "../hooks/useMineShelf";

// ---------------------------------------------------------------------------
// Local mode: solo editing from a seed, no auth
// ---------------------------------------------------------------------------

export function LocalRoomPage() {
  const { seedId = "" } = useParams();
  return <LocalRoom seedId={seedId} />;
}

/**
 * A seed's room, solo, signed out or in: /room/local/:seedId, or the home
 * page's stage (`stage`), where this week's Flip plays behind the first-visit
 * questions. On the stage it is not a remix until someone touches it, so it
 * does not report one; the page reports its own.
 */
export function LocalRoom({ seedId, stage }: { seedId: string; stage?: StageControls }) {
  const seed = useQuery({
    queryKey: ["web", "seed", seedId],
    queryFn: () => webApi<SeedApiDetail>(`/seeds/${encodeURIComponent(seedId)}`),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
    staleTime: Infinity,
  });

  // Build a CanonicalRoom from the seed's arrangement snapshot.
  const initialRoom = useMemo((): CanonicalRoom | null => {
    if (!seed.data) return null;
    const snapshot = seed.data.arrangement as Snapshot | null;
    if (!snapshot) return null;
    const plan = planHydration(snapshot, mintLocalId, () => true);
    return plan?.room ?? null;
  }, [seed.data]);

  const editor = useLocalEditor(initialRoom);

  useEffect(() => {
    if (seedId && !stage) trackAnalytics("remix_started", { seedId, detail: "browser" });
  }, [seedId, stage]);

  if (seed.isPending) return <LoadingScreen />;
  if (seed.isError) return <ErrorScreen seedId={seedId} />;

  const seedData = seed.data;
  const { tempo } = effectiveTempo(editor.room, seedData.bpm);

  return (
    <RoomShell
      seedId={seedId}
      title={seedData.title}
      backTo={seedPath(seedId)}
      room={editor.room}
      tempo={tempo}
      editor={editor}
      stage={stage}
    />
  );
}

// ---------------------------------------------------------------------------
// New seed: a room made from nothing, kept as a draft in this browser until
// its first Save makes it a seed
// ---------------------------------------------------------------------------

/** `/room/new`: a fresh draft, under an id of its own. */
export function NewRoomRedirect() {
  const [id] = useState(mintDraftId);
  return <Navigate to={draftRoomPath(id)} replace />;
}

/** A blank room: 120 BPM, eight empty scenes, nothing on the timeline. */
function blankRoom(): CanonicalRoom {
  return { ...emptyRoom(120), sceneNames: Array.from({ length: 8 }, () => null) };
}

export function BlankRoomPage() {
  const { draftId = "" } = useParams();
  const [draft, setDraft] = useState<RoomDraft | null | undefined>(undefined);
  useEffect(() => {
    let live = true;
    void loadDraft(draftId).then((d) => {
      if (live) setDraft(d);
    });
    return () => {
      live = false;
    };
  }, [draftId]);
  if (draft === undefined) return <LoadingScreen />;
  // A stale id with nothing stored starts blank rather than failing.
  return <BlankRoom key={draftId} draftId={draftId} restored={draft} />;
}

function BlankRoom({ draftId, restored }: { draftId: string; restored: RoomDraft | null }) {
  const navigate = useNavigate();
  const [initial] = useState(() => restored?.room ?? blankRoom());
  const editor = useLocalEditor(initial);
  const [title, setTitle] = useState(restored?.title ?? "");
  const takes = useTakeStore(undefined, restored?.takes);
  const saved = useRef(false);

  useEffect(() => {
    trackAnalytics("remix_started", { detail: restored ? "browser_draft" : "browser_new" });
  }, [restored]);

  // Kept as a draft on every change, a moment after it settles — once there
  // is something to keep.
  const room = editor.room;
  useEffect(() => {
    if (!room || saved.current || (room.tracks.length === 0 && title.trim() === "")) return;
    const t = window.setTimeout(() => {
      if (saved.current) return;
      const held = localTakeKeysOf(room).flatMap((key) => {
        const blob = takes.held.get(key);
        return blob ? [{ key, blob }] : [];
      });
      void saveDraft({ id: draftId, title, room, takes: held, updatedAt: Date.now() });
    }, 500);
    return () => window.clearTimeout(t);
  }, [room, title, draftId, takes]);

  const { tempo } = effectiveTempo(room, 120);
  return (
    <RoomShell
      seedId={null}
      newSeed={{
        onTitle: setTitle,
        takes,
        onSaved: (seedId) => {
          saved.current = true;
          void deleteDraft(draftId);
          navigate(`/room/local/${encodeURIComponent(seedId)}`, { replace: true });
        },
      }}
      title={title}
      backTo="/"
      room={room}
      tempo={tempo}
      editor={editor}
    />
  );
}

// ---------------------------------------------------------------------------
// Connected mode: multiplayer jam — editing signed in; listening in (/jams/:jamId) open to anyone
// ---------------------------------------------------------------------------

import { participantKey, type JamSummary, type JamParticipant } from "@shared/api-types.js";
import { useRoomSocket, type SocketStatus } from "@web-shared/hooks/useRoomSocket";
import { useRoomReplica } from "@web-shared/hooks/useRoomReplica";
import { useJamEditor } from "@web-shared/hooks/useJamEditor";
import { roomSources } from "@web-shared/lib/player-sources";
import { useSession } from "../session";
import { useAuthGate } from "../auth";
import { Avatar } from "../components/Avatar";

/**
 * The grid where the timeline's own zoom-adaptive one (ArrangementView) is not
 * in play: the pads, and the moment before the timeline has first measured
 * itself. A quarter note.
 */
const GRID = 1;

/**
 * `listen`: in the room as an observer (/jams/:jamId) — the same editor, the
 * same views and transport, nothing that writes, and Join / Leave where Save
 * would be. Join swaps the address for the room's own and the page carries
 * on (AccountApp renders both addresses with one component, so nothing
 * remounts and the audio already loaded stays loaded).
 */
export function ConnectedRoomPage({ listen = false }: { listen?: boolean }) {
  const { jamId = "" } = useParams();
  const session = useSession();
  const navigate = useNavigate();
  const gate = useAuthGate();

  // Fetch jam metadata to check status and determine role.
  const jam = useQuery({
    queryKey: ["web", "jam", jamId],
    queryFn: () => webApi<JamSummary>(`/jams/${encodeURIComponent(jamId)}`),
    retry: 1,
  });

  // A room that has ended sends you to where it left off (P2.5). Decided
  // here, rendered below the hooks: an open room that closes while you are in
  // it must not change how many hooks this component calls.
  const closed = jam.data?.status === "closed";

  // Role: host if you created the jam, otherwise guest.
  const isHost = session?.user.id === jam.data?.createdBy;
  const canEdit = Boolean(session) && !closed && !listen; // any signed-in user can edit for now

  // WebSocket + replica + editor — the full multiplayer stack.
  const socket = useRoomSocket(jamId, {
    edit: canEdit,
    onBatch: (payload) => replica.ingest(payload),
    onOpen: () => replica.refresh(),
  });
  const replica = useRoomReplica(jamId);
  const jamEditor = useJamEditor(jamId, replica, canEdit);

  // Wrap the JamEditor into the LocalEditor interface the shell expects.
  const editor: LocalEditor = useMemo(() => ({
    room: jamEditor.room,
    editDepth: 0,
    dirty: false, // connected mode: edits are server-side, not locally dirty
    propose: (intents) => {
      if (!canEdit) return false;
      void jamEditor.propose(intents);
      return true;
    },
    proposeUnrecorded: (intents) => {
      if (!canEdit) return false;
      void jamEditor.proposeUnrecorded(intents);
      return true;
    },
    busy: jamEditor.busy,
    canUndo: jamEditor.canUndo,
    canRedo: jamEditor.canRedo,
    undoLabel: jamEditor.undoLabel,
    redoLabel: jamEditor.redoLabel,
    undo: jamEditor.undo,
    redo: jamEditor.redo,
    markClean: () => {},
  }), [jamEditor, canEdit]);
  const listenControls = useMemo(
    () => (listen
      ? {
          // Listening in is open to anyone (a jam off a public seed); joining
          // — the address that edits — needs an account. Signed in, the gate
          // runs Join straight away.
          onJoin: () =>
            gate.requireSignIn("join_jam", "Sign in to join this jam", () =>
              navigate(roomPath(jamId), { replace: true }),
            ),
          onLeave: () => navigate("/jam"),
        }
      : undefined),
    [listen, navigate, jamId, gate],
  );

  const bpm = jam.data?.baseSeedId ? 120 : 120; // default, overridden by room tempo
  const { tempo } = effectiveTempo(editor.room, bpm);

  if (jam.isPending) return <LoadingScreen />;
  if (jam.isError) return <ErrorScreen seedId={jamId} />;
  if (closed) return <ClosedRoomRedirect seedId={jam.data.tipSeedId ?? jam.data.baseSeedId} />;

  return (
    <RoomShell
      seedId={jam.data.baseSeedId}
      title={jam.data.title}
      backTo="/jam"
      room={editor.room}
      tempo={tempo}
      editor={editor}
      presence={{ participants: socket.participants, status: socket.status, isHost, listening: listen }}
      jamId={jamId}
      usePublicAudio={false}
      listen={listenControls}
    />
  );
}

function ClosedRoomRedirect({ seedId }: { seedId: string }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 text-center">
      <p className="text-sm text-muted-foreground">This room has ended — here's where it left off.</p>
      <Button asChild size="sm">
        <Link to={seedPath(seedId)}>Go to seed</Link>
      </Button>
    </div>
  );
}

/**
 * The keys this room actually listens for.
 *
 * Its own list rather than the editor's: that one advertises locators, a piano
 * roll, device chains and time selections, none of which are on this page. A
 * sheet that lists a key which does nothing costs more than no sheet, because
 * the one that didn't work makes you doubt the ones that did.
 */
const ROOM_SHORTCUTS: ShortcutGroup[] = [
  {
    title: "Transport",
    rows: [
      ["Space", "play from the marker · stop, back to the marker"],
      ["⇧Space", "carry on from where it stopped"],
      ["⌥Space", "play the selection, then stop"],
      ["click a track · click a clip's body", "move the marker (the blue line) there, on the grid — and pick that lane for a paste"],
      ["click the ruler", "play from there; while playing, jump there on the next bar"],
      ["Home · Enter · double-click Stop", "the marker back to the start"],
    ],
  },
  {
    title: "Clips and time",
    rows: [
      ["click a clip's title bar · ⇧-click", "select the clip · add it to the selection"],
      ["drag a lane, or a clip's body", "select a span of time, across the lanes you cross — half a clip, a beat, a bar with a rest in it (clips wholly inside are selected too)"],
      ["⇧-click", "grow the time selection to the click"],
      ["hold a clip (touch)", "audio: open it in the clip editor · MIDI: add it to the selection"],
      ["drag a title bar · an edge", "move · trim (onto the grid; ⌘ drags free). A trim keeps the audio where it was"],
      ["⌥-drag a title bar", "copy the clip to the drop, same lane or another"],
      ["⌘C · ⌘X · ⌘V", "copy · cut · paste at the marker. A copied span pastes whole: its clips, cut at its edges, and its silence"],
      ["⌘D", "duplicate — right after the selection (a span: its own length later), which moves to the copy, so ⌘D again carries on"],
      ["⌘E", "split the selected clips at the marker · a time selection: cut at both its edges"],
      ["Delete", "delete the selected clips · a time selection: empty it, cutting clips at its edges"],
      ["⌘J", "consolidate: each lane's part of the selection joined into one clip, gaps included (MIDI merged as it plays; audio rendered as it sounds, dry)"],
      ["⌘L", "loop the selection (again to turn it off)"],
      ["← →  (⇧ ×4)", "move the selection along by the grid"],
      ["⇧← ⇧→", "a time selection one grid step shorter / longer"],
      ["⌘1 · ⌘2 · ⌘4", "grid narrower · wider · snap on / off (the grid follows the zoom, as Live's adaptive grid does)"],
      ["↑ ↓", "move it to the lane above / below (same kind)"],
      ["M", "mute / unmute the selected clips"],
      ["⌘A · Esc", "select every clip on the timeline · clear the selection"],
      ["⌘Z · ⇧⌘Z · ⌘Y", "undo · redo"],
      ["?", "this sheet"],
    ],
  },
];

const ROOM_SHORTCUTS_FOOTER =
  "As in Live, a clip moved, pasted, duplicated or trimmed onto another overwrites it: the one underneath is trimmed, split round it, or deleted if it's covered. A paste and a duplicate keep the selection's shape, in time and across lanes. Mute, solo and the faders on the Mix tab are the Set's; what you hear in this browser is not.";

/**
 * Whether a keystroke belongs to something other than the room's shortcuts.
 *
 * Anything being typed into owns its keys — the material shelf's search box, a
 * fader (where ← and → move the fader, which is what a focused fader should do),
 * a renamed lane. So does a dialog that has trapped focus: Escape closes
 * PublishRoom rather than clearing a selection behind it.
 */
/** An audio clip (timeline or Session View) and its lane, by id. */
function findEditableAudio(arrangement: Arrangement | null, clipId: string): { clip: LaneClip; lane: Lane } | null {
  for (const lane of arrangement?.lanes ?? []) {
    const clip = lane.clips.find((c) => c.id === clipId) ?? lane.sessionClips.find((c) => c.id === clipId);
    if (clip && clip.kind !== "midi") return { clip, lane };
  }
  return null;
}

function typingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return (
    el.isContentEditable ||
    /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) ||
    (el.closest?.("[role=dialog]") ?? null) !== null
  );
}

/** Somewhere a Space is a character: a text field, a text area, a picker.
 * Sliders, checkboxes and buttons are not — Space there is the transport's. */
function textEntry(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  if (el.isContentEditable || el.tagName === "TEXTAREA" || el.tagName === "SELECT") return true;
  if (el.tagName !== "INPUT") return false;
  const type = (el as HTMLInputElement).type;
  return ["text", "search", "number", "email", "password", "url", "tel"].includes(type);
}

/** Inside a modal (a publish sheet, the shortcuts help): not the room's keys. */
function inModal(target: EventTarget | null): boolean {
  return ((target as HTMLElement | null)?.closest?.('[aria-modal="true"]') ?? null) !== null;
}

/** In a modal that leaves Space to the transport (the clip editor's sheet). */
function inTransportModal(target: EventTarget | null): boolean {
  const modal = (target as HTMLElement | null)?.closest?.('[aria-modal="true"]');
  return modal instanceof HTMLElement && modal.dataset.transportKeys !== undefined;
}

// ---------------------------------------------------------------------------
// Shared room shell: the layout both modes use
// ---------------------------------------------------------------------------

interface PresenceInfo {
  participants: JamParticipant[] | null;
  status: SocketStatus;
  isHost: boolean;
  /** In the room as an observer: shown as "listening", not host or guest. */
  listening?: boolean;
}

/** Listen mode's two ways on: into the jam, or out of the room. */
interface ListenControls {
  onJoin: () => void;
  onLeave: () => void;
}

/** A room made from nothing (BlankRoomPage): no seed yet, a title being
 * typed, and a Save that makes the seed. */
interface NewSeedControls {
  onTitle: (title: string) => void;
  onSaved: (seedId: string) => void;
  /** The tab's takes, owned by the page so its draft can keep them. */
  takes: TakeStore;
}

/** What the home page needs from the room it stages (LocalRoom's `stage`). */
export interface StageHandle {
  /** Stop it, if it is — something else is about to play. */
  stop: () => void;
  /** Ask before leaving unsaved edits behind: true to go ahead. */
  ask: () => Promise<boolean>;
}

export interface StageControls {
  /** In place of the back link: the way back to the home modal. */
  entry: ReactNode;
  onHandle: (handle: StageHandle) => void;
}

interface RoomShellProps {
  /** Null for a room made from nothing (`newSeed` says what to do instead). */
  seedId: string | null;
  newSeed?: NewSeedControls;
  title: string;
  backTo: string;
  room: CanonicalRoom | null;
  tempo: number;
  editor: LocalEditor;
  /** Connected mode: presence roster. */
  presence?: PresenceInfo;
  /** Connected mode: the jam whose room this IS. A save publishes it; local
   * mode has no jam until one is made to carry the upload. */
  jamId?: string;
  /** False in connected mode: use objectUrl (same-origin proxy with auth). */
  usePublicAudio?: boolean;
  /** Listening in: everything that writes is gone, Join and Leave in its place. */
  listen?: ListenControls;
  /** The home page's stage: no back link, and a handle for the page. */
  stage?: StageControls;
}

const isBoolean = (v: unknown): v is boolean => typeof v === "boolean";

/** How long a fader moved here keeps leading the room's value. */
const LEVEL_HOLD_MS = 800;
/** Two presses on one clip inside this: a double-tap, which opens the editor. */
const DOUBLE_PRESS_MS = 350;

function RoomShell({ seedId, newSeed, title, backTo, room, tempo, editor, presence, jamId, usePublicAudio = true, listen, stage }: RoomShellProps) {
  const listening = listen !== undefined;
  useDocumentTitle(title ? `${title} — ${listening ? "listening" : "editing"}` : "");

  // Land intents the way Live does (lib/live-edits.ts): whatever they cover on
  // their lanes is trimmed, split or deleted first, all as one undo step.
  // `moving` are the clips travelling with them, never cleared. Read through a
  // ref so the callers (drag handlers, the shelf) don't rebuild per frame.
  const [editNote, setEditNote] = useState<string | null>(null);
  useEffect(() => {
    if (!editNote) return;
    const t = window.setTimeout(() => setEditNote(null), 4000);
    return () => window.clearTimeout(t);
  }, [editNote]);
  const landing = useRef({ room, tempo, editor });
  landing.current = { room, tempo, editor };
  const proposeOverwrite = useCallback((placements: Intent[], moving: readonly string[] = [], clear: readonly LaneSpan[] = []): boolean => {
    const { room: now, tempo: bpm, editor: ed } = landing.current;
    if (!now || placements.length + clear.length === 0) return false;
    const intents = overwrite(now, placements, bpm, moving, clear);
    if (intents && intents.length === 0) return true;
    if (!intents) {
      setEditNote("Something in the way can't be cut there: a transposed unwarped clip only cuts once it's warped.");
      return false;
    }
    ed.propose(intents);
    return true;
  }, []);

  // Recorded takes: uploaded to the jam, or held in this tab until a save
  // makes one (useTakeStore). A local room reads both kinds through one URL.
  const ownTakes = useTakeStore(jamId);
  const takes = newSeed?.takes ?? ownTakes;
  const { urlOf: takeUrlOf } = takes;
  // A clip placed from the producer's own library plays through their signed
  // URL until a save makes it a seed's (the public endpoint serves only
  // blobs some seed has published).
  const { urlOfKey: myClipUrlOf } = useMyClips();
  const localAudioUrl = useCallback(
    (audioKey: string) => takeUrlOf(audioKey) ?? myClipUrlOf(audioKey) ?? publicAudioUrl(audioKey),
    [takeUrlOf, myClipUrlOf],
  );

  // Where a clip's object is fetched from. One definition for the shelf's
  // previews, the pads' launches and the variant probe, so none of them can
  // be looking at a different object than the others.
  const audioUrlOf = useCallback(
    (audioKey: string) => (usePublicAudio ? localAudioUrl(audioKey) : objectUrl(audioKey)),
    [usePublicAudio, localAudioUrl],
  );

  // Two clips that describe themselves identically but point at different
  // renders: ask the audio which of them are really the same piece. Costs
  // nothing on a Set where no two clips collide.
  const fetchesSettled = useRef<() => Promise<void>>(() => Promise.resolve());
  const whenFetchesSettled = useCallback(() => fetchesSettled.current(), []);
  const variantOf = useMaterialVariants(room, audioUrlOf, whenFetchesSettled);
  // The arrangement with ghost slots (session view loops from timeline clips).
  const rawArrangement = useMemo((): Arrangement | null => {
    if (!room) return null;
    return fromRoom(room.tracks, room.clips, room.sceneNames ?? undefined);
  }, [room]);
  const arrangement = useMemo(
    // `afterScenes`: the Pads screen draws the two kinds of row as separate
    // bands, so ghosts must not sit in rows the Set's own scenes are numbered
    // by — otherwise the scene band reads "1, 4, 5, 6, 7, 8".
    () => (rawArrangement ? withGhostSlots(rawArrangement, { startRow: "afterScenes", variantOf }) : null),
    [rawArrangement, variantOf],
  );

  const arrangementRef = useRef(arrangement);
  arrangementRef.current = arrangement;

  // Build player sources. Local mode uses the public audio endpoint
  // (/api/audio/blob/:hash, no auth). Connected mode uses roomSources which
  // calls objectUrl (same-origin proxy, needs auth — user is signed in).
  const sources = useMemo(() => {
    if (!arrangement) return [];
    return usePublicAudio
      ? roomSourcesWithUrls(arrangement, tempo, localAudioUrl)
      : roomSources(arrangement, tempo);
  }, [arrangement, tempo, usePublicAudio, localAudioUrl]);

  // Each lane's MIDI effects (a chord, an arp, a random — the room's device
  // chains), applied to its clips' notes as Live's chain runs them. viz
  // passes these too; the room didn't, so a chord lane sounded different
  // in each app (playback-audit §1.6).
  const midiEffectsByLane = useMemo(() => {
    const out: Record<string, readonly MidiStage[]> = {};
    for (const t of room?.tracks ?? []) {
      if (!t.devices) continue;
      const stages = midiStagesFor(t.devices);
      if (stages.length > 0) out[t.id] = stages;
    }
    return out;
  }, [room?.tracks]);
  const midi = useMemo(() => {
    if (!arrangement) return [];
    return midiSources(arrangement, tempo, midiEffectsByLane);
  }, [arrangement, tempo, midiEffectsByLane]);

  // Waveforms come from the objects' small previews (`requestPeaks`), so
  // the objects themselves are fetched behind them, ready for Play.
  const instruments = useMemo(() => (arrangement ? laneInstruments(arrangement) : {}), [arrangement]);
  // A clip plays its OWN object (a pad, a preview), and in the browser room that object is
  // served by the public blob endpoint rather than by `objectUrl`. Everything
  // else about the launch — the brace, the warp, the stretch to the Set's
  // tempo — is `clipAudition`'s, shared with every other surface that
  // launches or previews, so a pad here and a pad on the seed page cannot disagree.
  const slotSource = useCallback(
    (clip: LaneClip, lane: Lane) => {
      const src = clipAuditionSource(lane, clip, tempo, { effects: midiEffectsByLane[lane.id] });
      if (!src || "notes" in src) return src;
      if (!usePublicAudio) return src;
      const url = clip.audioKey ? localAudioUrl(clip.audioKey) : undefined;
      return url ? { ...src, url } : null;
    },
    [tempo, usePublicAudio, localAudioUrl, midiEffectsByLane],
  );

  // Everything that sounds goes through one facade: Transport, Launch,
  // Preview (web-shared/hooks/usePlayback.ts) — so ▶, ■, Space, a pad and
  // the editor's play mean one thing each, on every surface.
  const playback = usePlayback(
    sources,
    arrangement ? beatsToSeconds(arrangement.length, tempo) : 0,
    midi,
    { prefetch: true, instruments, arrangement, bpm: tempo, sourceFor: slotSource, midiEffectsByLane },
  );
  const { player, launch } = playback;
  fetchesSettled.current = player.fetchesSettled;

  // Sync tempo to the player.
  const { setTempo } = player;
  useEffect(() => setTempo(tempo), [setTempo, tempo]);

  // Launch quantize — Live's three, plus the multi-bar values a loop-led Set
  // wants. It is the launch AND the stop: both land on the same lines.
  const [padQuantize, setPadQuantize] = useState<PadQuantize>("bar");
  const { setLaunchQuantize: applyQuantize } = player;
  useEffect(() => applyQuantize(padQuantize), [applyQuantize, padQuantize]);

  // Waveforms, lanes and pads alike: the object the transport would play,
  // windowed in object time the way it plays it. Before the first Play the
  // transport has fetched nothing, so a view asks for what it can't draw and
  // the player fetches just enough to summarise it (`ensurePeaks`).
  //
  // A waveform first, then the exact one: Live's clip-edge fades give every
  // copy of a loop its own render, so a loop placed sixteen times is sixteen
  // objects that draw near-identically. Each clip draws from its OWN object
  // once that has loaded, and until then from the first object of its
  // material on its track — the shelf's grouping, variants and all
  // (useMaterialVariants), so a transposed duplicate that only CLAIMS to be
  // the same loop never stands in for it. The shared object is fetched first;
  // each clip's own queues behind every waveform still missing.
  const { peaks, ensurePeaks, decodedVersion } = player;
  const drawnObject = useMemo(() => {
    const first = new Map<string, string>();
    const out = new Map<string, string>();
    for (const c of room?.clips ?? []) {
      if (!c.audioKey || c.clipKind === "midi") continue;
      const key = `${c.trackId}|${variantKey(roomClipFacts(c), variantOf(c.audioKey))}`;
      const shared = first.get(key);
      if (shared === undefined) first.set(key, c.audioKey);
      else if (shared !== c.audioKey) out.set(c.audioKey, shared);
    }
    return out;
  }, [room, variantOf]);
  const urlOfKey = useCallback(
    (key: string): string | undefined => (usePublicAudio ? localAudioUrl(key) : objectUrl(key)),
    [usePublicAudio, localAudioUrl],
  );
  /** What a waveform is drawn from instead of the object: its MP3 preview.
   * A pool object only (a take recorded here has none), and only one the
   * API says has a preview (usePoolPreviews, one request for the room):
   * the page used to request every blob's and take a 404 for each uploaded
   * without one (#125). A preview the lookup could not rule out is still
   * requested, and the player falls back to the object if it 404s. */
  const poolPreviews = usePoolPreviews(room?.clips);
  const previewOfKey = useCallback(
    (key: string): string | undefined => {
      const hash = poolHashAnyChannel(key);
      if (hash === null || poolPreviews.hasPreview(hash) === false) return undefined;
      if (usePublicAudio) {
        if (takeUrlOf(key)) return undefined;
        const url = publicAudioUrl(key);
        return url === undefined ? undefined : `${url}/preview`;
      }
      const preview = previewKeyOf(key);
      return preview === null ? undefined : objectUrl(preview);
    },
    [usePublicAudio, takeUrlOf, poolPreviews],
  );
  /** A pool blob the preview lookup has not answered for yet. */
  const previewUndecided = useCallback(
    (key: string | null | undefined): boolean => {
      if (!key) return false;
      const hash = poolHashAnyChannel(key);
      return hash !== null && poolPreviews.hasPreview(hash) === undefined;
    },
    [poolPreviews],
  );
  /** The clip's own object, then its stand-in where it has one. */
  const clipObjectUrls = useCallback(
    (clip: LaneClip): { own?: string; shared?: string } => {
      if (!clip.audioKey) return {};
      const shared = drawnObject.get(clip.audioKey);
      return { own: urlOfKey(clip.audioKey), shared: shared === undefined ? undefined : urlOfKey(shared) };
    },
    [drawnObject, urlOfKey],
  );
  const objectPeaks = useCallback(
    (clip: LaneClip, buckets: number, fromBeat: number, toBeat: number): Float32Array | null => {
      const { own, shared } = clipObjectUrls(clip);
      const span = objectFileSpan(clip.geometry, fromBeat, toBeat, tempo);
      const from = span.fromSec;
      const length = Math.max(0, span.toSec - span.fromSec);
      return (own ? peaks(own, buckets, from, length) : null) ?? (shared ? peaks(shared, buckets, from, length) : null);
    },
    [clipObjectUrls, peaks, tempo],
  );
  const requestPeaks = useCallback(
    (clip: LaneClip) => {
      const { own, shared } = clipObjectUrls(clip);
      const sharedKey = clip.audioKey ? drawnObject.get(clip.audioKey) : undefined;
      // Not yet known whether the blob has a preview: neither guess is free
      // (the object is megabytes; a missing preview is the 404). The views
      // ask again when the lookup lands, since this callback rebuilds then.
      if (previewUndecided(clip.audioKey) || previewUndecided(sharedKey)) return;
      if (shared && sharedKey) ensurePeaks(shared, "now", previewOfKey(sharedKey));
      if (own && clip.audioKey) ensurePeaks(own, shared ? "later" : "now", previewOfKey(clip.audioKey));
    },
    [clipObjectUrls, ensurePeaks, drawnObject, previewOfKey, previewUndecided],
  );
  const laneWaveform: WaveformSource = useMemo(
    () => ({
      peaksFor: (clip, _laneId, buckets) => {
        const from = clip.geometry ? objectStart(clip.geometry) : 0;
        return objectPeaks(clip, buckets, from, from + clip.duration);
      },
      request: requestPeaks,
      version: decodedVersion,
    }),
    [objectPeaks, requestPeaks, decodedVersion],
  );
  // A pad draws what a launch loops: the brace for a looped clip, the clip
  // from its head otherwise.
  const padWaveform: PadWaveformSource = useMemo(
    () => ({
      peaksFor: (clip, _lane, buckets) => {
        const g = clip.geometry;
        if (g && isLooped(g)) return objectPeaks(clip, buckets, Math.max(0, g.loopStart), g.loopEnd);
        const from = g ? objectStart(g) : 0;
        return objectPeaks(clip, buckets, from, from + clip.duration);
      },
      request: requestPeaks,
      version: decodedVersion,
    }),
    [objectPeaks, requestPeaks, decodedVersion],
  );

  const { backToArrangement } = launch;
  useEffect(() => () => backToArrangement(), [backToArrangement]);

  // Mute / solo — routed through the editor so changes persist in room state.
  const [muted, setMuted] = useState<Set<string>>(new Set());
  const [soloed, setSoloed] = useState<Set<string>>(new Set());

  const proposeMute = useCallback((id: string, m: boolean) => {
    setMuted((prev) => { const n = new Set(prev); m ? n.add(id) : n.delete(id); return n; });
    player.setMuted(id, m);
    if (room) editor.propose([{ kind: "setTrackField", id, baseRev: room.tracks.find((t) => t.id === id)?.rev ?? 0, field: "mute" as TrackField, value: m }]);
  }, [player, room, editor]);

  const proposeSolo = useCallback((id: string, s: boolean) => {
    setSoloed((prev) => { const n = new Set(prev); s ? n.add(id) : n.delete(id); return n; });
    player.setSoloed(id, s);
    if (room) editor.propose([{ kind: "setTrackField", id, baseRev: room.tracks.find((t) => t.id === id)?.rev ?? 0, field: "solo" as TrackField, value: s }]);
  }, [player, room, editor]);

  // The Set's mute and solo are what this page plays: a track muted from
  // Live (or by anyone else in the jam) arrives as room state, not through
  // proposeMute, so the gates follow the room. Without this the header showed
  // the track muted while its audio played on.
  const { setMuted: gateMuted, setSoloed: gateSoloed } = player;
  const laneGateKey = useMemo(
    () => (arrangement ?? { lanes: [] }).lanes.map((l) => `${l.id}:${l.mute ? 1 : 0}:${l.solo ? 1 : 0}`).join("|"),
    [arrangement],
  );
  useEffect(() => {
    if (!arrangement) return;
    for (const lane of arrangement.lanes) {
      gateMuted(lane.id, lane.mute);
      gateSoloed(lane.id, lane.solo);
    }
    setMuted(new Set(arrangement.lanes.filter((l) => l.mute).map((l) => l.id)));
    setSoloed(new Set(arrangement.lanes.filter((l) => l.solo).map((l) => l.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laneGateKey, gateMuted, gateSoloed]);

  const audio: LaneAudioState = useMemo(() => ({
    muted,
    soloed,
    onMute: proposeMute,
    onSolo: proposeSolo,
    // proposeMute/proposeSolo edit the Set — the header's M/S already do this.
    mixesTheSet: true,
    overridden: player.overridden,
    onBackToArrangement: (laneId: string) => launch.backToArrangement(laneId),
    // Each MIDI lane's instrument is the seed's: picking one is an edit.
    patches: instruments,
    ...(listening
      ? {}
      : {
          onPatch: (laneId: string, id: string) => {
            const intent = room && isInstrumentId(id) ? setTrackInstrument(room, laneId, id) : null;
            if (intent) editor.propose([intent]);
          },
        }),
  }), [muted, soloed, proposeMute, proposeSolo, player, launch, instruments, listening, room, editor]);

  // Per-lane levels, seeded from the arrangement.
  const [levels, setLevels] = useState<Record<string, LaneLevel>>({});
  const [masterVolume, setMasterVolume] = useState(1);
  const { setLaneLevel } = player;
  const laneLevelKey = useMemo(
    () => (arrangement ?? { lanes: [] }).lanes.map((l) => `${l.id}:${l.volume}:${l.pan}`).join("|"),
    [arrangement],
  );
  // A fader held here leads the room for a moment (its own proposals echo
  // back older positions mid-drag); once it has rested, the room's level is
  // the level again — so a fader moved in Live after one moved here still
  // arrives. The override used to be kept for good, which deafened the lane
  // to every later change from anyone else.
  const levelTouchedAt = useRef(new Map<string, number>());
  const levelSettleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [levelSettle, setLevelSettle] = useState(0);
  useEffect(() => () => { if (levelSettleTimer.current) clearTimeout(levelSettleTimer.current); }, []);
  useEffect(() => {
    if (!arrangement) return;
    const now = Date.now();
    const rested: string[] = [];
    for (const lane of arrangement.lanes) {
      const held = now - (levelTouchedAt.current.get(lane.id) ?? 0) < LEVEL_HOLD_MS;
      if (held && levels[lane.id]) {
        setLaneLevel(lane.id, levels[lane.id]!);
        continue;
      }
      setLaneLevel(lane.id, { volume: lane.volume, pan: lane.pan });
      if (levels[lane.id]) rested.push(lane.id);
    }
    if (rested.length > 0) {
      setLevels((prev) => {
        const next = { ...prev };
        for (const id of rested) delete next[id];
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laneLevelKey, setLaneLevel, levelSettle]);

  const applyLevel = useCallback(
    (laneId: string, level: LaneLevel) => {
      setLevels((prev) => ({ ...prev, [laneId]: level }));
      setLaneLevel(laneId, level);
      levelTouchedAt.current.set(laneId, Date.now());
      if (levelSettleTimer.current) clearTimeout(levelSettleTimer.current);
      levelSettleTimer.current = setTimeout(() => setLevelSettle((n) => n + 1), LEVEL_HOLD_MS + 50);
      // Persist volume/pan in the room state.
      if (room) {
        const track = room.tracks.find((t) => t.id === laneId);
        if (track) {
          const intents: import("@shared/sync/intents.js").Intent[] = [];
          if (level.volume !== track.volume) intents.push({ kind: "setTrackField", id: laneId, baseRev: track.rev, field: "volume", value: level.volume });
          if (level.pan !== track.pan) intents.push({ kind: "setTrackField", id: laneId, baseRev: track.rev, field: "pan", value: level.pan });
          if (intents.length > 0) editor.propose(intents);
        }
      }
    },
    [setLaneLevel, room, editor],
  );

  // Where a tapped placement lands. Read every frame the transport moves, so
  // the shelf's ＋ puts material where the playhead actually is.
  const playheadBeats = (player.position * tempo) / 60;

  /**
   * The shelf's ＋: the same copy a drag makes, on the material's OWN track at
   * the playhead.
   *
   * A drag is a pointer's privilege — HTML5 drag events never fire for a
   * thumb — so without this the shelf would be a read-only list on every
   * phone. Its own track is the one placement that needs no aim: the material
   * came from there, so the lane exists and is the right kind by
   * construction.
   */
  const placeMaterial = useCallback(
    (clipId: string) => {
      if (!room) return;
      const source = room.clips.find((c) => c.id === clipId);
      if (!source) return;
      // Live's habit: at the insert marker, over whatever is there.
      const at = (player.insertSec * tempo) / 60;
      const intent = copyClipToLane(room, clipId, source.trackId, Math.max(0, at), (start) => start);
      if (intent) proposeOverwrite([intent]);
    },
    [room, player.insertSec, tempo, proposeOverwrite],
  );

  // ---- The Session View -----------------------------------------------------

  // Live's grid, as rows: the Set's own scenes and slots, with the timeline's
  // loops ghosted in below them. NOT a reduction of the timeline — a track
  // with nothing on its timeline still has its slot clips here, and a row is
  // the scene the producer put those clips in.
  const scenes = useMemo(() => (arrangement ? buildSceneGrid(arrangement) : null), [arrangement]);

  // Everything the room is built from, once each — the timeline's clips and
  // the Session View's together (`buildRoomClipLibrary` groups by material,
  // not by placement). Built off the ROOM, not the ghosted arrangement: a
  // ghost is a copy of a timeline clip made for the pad grid, and the shelf
  // must not offer the same piece twice.
  const library = useMemo(
    () =>
      room
        ? buildRoomClipLibrary(room.tracks, room.clips, tempo, {}, audioUrlOf, variantOf)
        : { tracks: [], distinctClips: 0, totalPlacements: 0, unplayableTracks: [] },
    [room, tempo, audioUrlOf, variantOf],
  );

  const padLaunch: PadLaunchState = useMemo(
    () => ({
      playing: player.sessionPlaying,
      stopping: player.sessionStopping,
      overridden: player.overridden,
      // The callbacks, not `launch` itself: that is a new object every
      // render, and the pads are memoised on this.
      onLaunch: launch.launchClip,
      onLaunchScene: launch.launchScene,
      onStopLane: launch.stopLane,
      onStopAll: launch.stopAll,
      onBackToArrangement: launch.backToArrangement,
      positionSec: player.slotPositionSec,
      periodSec: player.slotPeriodSec,
    }),
    [
      player.sessionPlaying,
      player.sessionStopping,
      player.overridden,
      player.slotPositionSec,
      player.slotPeriodSec,
      launch.launchClip,
      launch.launchScene,
      launch.stopLane,
      launch.stopAll,
      launch.backToArrangement,
    ],
  );

  const padMixer: PadMixerState = useMemo(
    () => ({
      muted,
      soloed,
      onMute: proposeMute,
      onSolo: proposeSolo,
      onVolume: (laneId: string, volume: number) => {
        const lane = arrangement?.lanes.find((l) => l.id === laneId);
        applyLevel(laneId, { volume, pan: levels[laneId]?.pan ?? lane?.pan ?? 0 });
      },
    }),
    [muted, soloed, proposeMute, proposeSolo, applyLevel, levels, arrangement],
  );

  // ---- Editing state --------------------------------------------------------

  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Live's time selection — a span over lanes — and the lane a click last put
  // the insert marker on. What copy, paste and duplicate act on when set.
  const [timeSelection, setTimeSelection] = useState<TimeSpan | null>(null);
  const [cursorLaneId, setCursorLaneId] = useState<string | null>(null);
  // Live's grid: zoom-adaptive, ⌘1 / ⌘2 narrower / wider, ⌘4 snap on / off.
  // `grid` is what the timeline resolved it to at its current zoom (0 = off),
  // so a nudge, a trim's shortest length and a shelf drop agree with what is
  // drawn.
  const [gridDensity, setGridDensity] = useState(1);
  const [snapOn, setSnapOn] = useState(true);
  const [grid, setGrid] = useState(GRID);
  const gridRef = useRef(grid);
  gridRef.current = grid;

  // The latest room and tempo, for callbacks that must not be rebuilt on
  // every edit (and for work that resumes after an await).
  const roomRef = useRef(room);
  roomRef.current = room;
  const tempoRef = useRef(tempo);
  tempoRef.current = tempo;

  // ■: the transport back to the insert marker and every preview silenced;
  // launched pads play on, as Live's Stop leaves the Session View running.
  // ■ ■ is the panic: everything, pads too, and the marker back to the start.
  const stopAll = playback.transport.stop;

  // Selecting on the timeline moves Live's insert marker to where the
  // selection starts, so Space plays what was just picked. Session View clips
  // have no place on the timeline and leave it where it is.
  const { setInsert } = player;
  const insertAtClips = useCallback(
    (clipIds: readonly string[]) => {
      const starts = (roomRef.current?.clips ?? [])
        .filter((c) => clipIds.includes(c.id) && c.slot === undefined)
        .map((c) => c.startTime);
      if (starts.length > 0) setInsert(beatsToSeconds(Math.min(...starts), tempoRef.current));
    },
    [setInsert],
  );

  // The clip editor's two sizes, on every width: minimized (one strip — the
  // clip's colour, name, play, ✕) and open (docked on a wide screen; the
  // sheet's half / full on a phone). A press loads a clip into the strip; a
  // double-click, a double-tap or a hold opens it (Logic's and GarageBand's
  // habit: a click selects, a double-click edits). Once open, it follows
  // every press until it is minimized again.
  const narrow = useIsCompact();
  const short = useIsShort() && narrow;
  const editorSheet = useSheetSnap("peek");
  const { show: showEditor } = editorSheet;
  // A phone on its side: half of 390px is the sheet's own header, tabs and
  // ruler, nothing to edit — so it opens to full there.
  const openSnap: SheetSnap = short ? "full" : "half";
  /** Load a clip AND open the editor on it — a double-click, a hold, a new clip. */
  const openEditor = useCallback((clipId: string) => {
    setEditingClipId(clipId);
    showEditor(openSnap);
  }, [showEditor, openSnap]);
  // A second press on the same clip soon after the first: a double-tap. A
  // mouse gets dblclick too; a finger doesn't, everywhere.
  const lastPress = useRef<{ id: string; at: number } | null>(null);
  const pressOpens = useCallback((clipId: string): boolean => {
    const now = performance.now();
    const again = lastPress.current?.id === clipId && now - lastPress.current.at < DOUBLE_PRESS_MS;
    lastPress.current = again ? null : { id: clipId, at: now };
    return again;
  }, []);

  /** Set by a timeline press that opens the editor; read once by the open effect. */
  const openedByPress = useRef(false);
  const onSelect = useCallback((clipId: string | null, additive: boolean) => {
    // A press on one clip loads it into the editor (Live: a click shows it in
    // Clip View) — minimized, unless it is already open or this is the second
    // press of a double. On the gesture, not the derived selection, so
    // pressing the same clip again after ✕ brings it back. The press places
    // the insert marker itself (where the finger landed), so the open must
    // not move it.
    if (clipId && !additive) {
      openedByPress.current = true;
      setEditingClipId(clipId);
      if (pressOpens(clipId)) showEditor(openSnap);
    }
    if (!clipId) { setSelected(new Set()); return; }
    if (!additive) insertAtClips([clipId]);
    setSelected((prev) => {
      if (additive) { const n = new Set(prev); n.has(clipId) ? n.delete(clipId) : n.add(clipId); return n; }
      return new Set([clipId]);
    });
  }, [insertAtClips, pressOpens, showEditor, openSnap]);

  const onSelectMany = useCallback((clipIds: string[], additive: boolean) => {
    if (!additive) insertAtClips(clipIds);
    setSelected((prev) => additive ? new Set([...prev, ...clipIds]) : new Set(clipIds));
  }, [insertAtClips]);

  const editState: EditState | undefined = useMemo(() => {
    if (!room) return undefined;
    return {
      selected,
      pending: new Set<string>(),
      grid: GRID,
      adaptiveGrid: { density: gridDensity },
      snap: snapOn,
      onGridResolved: setGrid,
      onSnapToggle: () => setSnapOn((on) => !on),
      onSelect,
      onSelectMany,
      onTimeSelect: setTimeSelection,
      timeSelection,
      onCursorLane: setCursorLaneId,
      // Live's rules (lib/live-edits.ts): a clip landing on another
      // overwrites it, and a trimmed edge keeps the audio where it was.
      onMove: (clipIds, deltaBeats, targetLaneId) => {
        const moves = moveClips(room, clipIds, deltaBeats, targetLaneId ? () => targetLaneId : undefined);
        // The span that selected them stays behind in Live; the clips leave it.
        if (proposeOverwrite(moves, clipIds)) setTimeSelection(null);
      },
      onCopyMove: (clipIds, deltaBeats, targetLaneId) => {
        if (proposeOverwrite(copyClips(room, clipIds, deltaBeats, targetLaneId ? () => targetLaneId : undefined))) setTimeSelection(null);
      },
      // Dropped below the last lane: the clip (or its ⌥-copy) on a new track.
      onMoveToNewLane: (clipId, deltaBeats, copy) => {
        const c = room.clips.find((x) => x.id === clipId);
        if (!c) return;
        const intents = copy
          ? copyClipToNewLane(room, clipId, nextOrd(room), c.startTime + deltaBeats)
          : moveClipToNewLane(room, clipId, nextOrd(room), deltaBeats);
        if (intents.length > 0 && editor.propose(intents)) setTimeSelection(null);
      },
      onResize: (clipId, edge, deltaBeats) => {
        const c = room.clips.find((x) => x.id === clipId);
        if (!c) return;
        const beat = edge === "start" ? c.startTime + deltaBeats : c.startTime + c.duration + deltaBeats;
        const intent = trimClip(room, clipId, edge, beat, tempoRef.current, gridRef.current > 0 ? gridRef.current : 1 / 16);
        if (intent) proposeOverwrite([intent]);
      },
      onTrackField: (laneId, field, value) => {
        // Sync player gain nodes for mute/solo/volume/pan.
        if (field === "mute") proposeMute(laneId, value as boolean);
        else if (field === "solo") proposeSolo(laneId, value as boolean);
        else if (field === "volume" || field === "pan") {
          const track = room.tracks.find((t) => t.id === laneId);
          if (track) {
            const level = { volume: track.volume, pan: track.pan, [field]: value as number };
            applyLevel(laneId, level);
          }
        } else {
          const intent = setTrackField(room, laneId, field, value);
          if (intent) editor.propose([intent]);
        }
      },
      onDeleteTrack: (laneId) => {
        const intent = deleteTrack(room, laneId);
        if (intent) editor.propose([intent]);
      },
      onCreateMidiClip: () => {}, // replaced in `edit`, once the creation hook exists
      // A piece dragged off the material shelf: a COPY onto whichever lane
      // took the drop, nudged forward to the first gap that fits rather than
      // landing on top of what is already there.
      onDropMaterial: (laneId, atBeat, clipId) => {
        // Already on the grid: the view snapped the drop.
        const intent = copyClipToLane(room, clipId, laneId, Math.max(0, atBeat), (start) => start);
        if (intent) proposeOverwrite([intent]);
      },
    };
  }, [room, selected, onSelect, onSelectMany, editor, proposeOverwrite, timeSelection, gridDensity, snapOn]);

  // A clip the room no longer has cannot be selected. Undo never resurrects:
  // an undone delete brings the clip back with a NEW id (lib/undo.ts), and a
  // create the room refused never had one — so without this the selection
  // accumulates ids that ring nothing and count as something.
  useEffect(() => {
    if (!room) return;
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const live = new Set(room.clips.map((c) => c.id));
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [room]);


  // Where the studio opens: the arrangement on a tablet or a computer, as a
  // DAW does; the pads on a phone, where a finger plays and a timeline is
  // cramped. The line is useIsCompact's — under 640px wide, or a phone on its
  // side (coarse pointer, under 500px tall) — so a portrait iPad (768+) opens
  // in Arrange. The same for a seed, a jam and the home page's stage. Read
  // once, at mount: a rotation does not yank the view away.
  const [view, setView] = useState<RoomView>(() => (narrow ? "pads" : "timeline"));
  // What a saved version plays: the same offline mixdown the download makes,
  // through the same lane mutes, levels and pans — but ignoring a solo
  // somebody left on, because a listening choice is not a mix. (This passed
  // `false` until 2026-10-10, which opened every gate: a muted track was
  // saved into the master.)
  const { renderMixdown } = player;
  const renderMaster = useCallback(() => renderMixdown(undefined, undefined, "mutes"), [renderMixdown]);
  // Out of the editor: a clip (its file, or as it sounds) and the whole room.
  const downloads = useRoomDownloads({ arrangement, tempo, title, seedId, urlOfKey, player, renderMaster });

  // The material shelf. Open beside the timeline on a wide screen, where it
  // costs nothing; shut on a phone, where it is a sheet over the thing the tab
  // is actually for. Read once, at mount: a rotation should not throw away
  // whichever state the producer chose.
  // The room the timeline has, for its lanes to grow into (ArrangementView's
  // fillHeight) — the box it scrolls in, less the New track row under it.
  // The phone mixer's lane with its pan showing, one at a time.
  const [mixPanLane, setMixPanLane] = useState<string | null>(null);
  const [timelineBox, setTimelineBox] = useState<HTMLDivElement | null>(null);
  const [timelineRoom, setTimelineRoom] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (!timelineBox) return;
    const measure = () => {
      const below = timelineBox.querySelector<HTMLElement>("[data-new-track-row]")?.offsetHeight ?? 0;
      setTimelineRoom(timelineBox.clientHeight - below);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(timelineBox);
    return () => ro.disconnect();
  }, [timelineBox]);
  const [libraryOpen, setLibraryOpen] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches,
  );
  // On a phone the shelf and the clip editor are two sheets in one column
  // (hooks/useSheetSnap): peek, half, full, the header as the handle. Only
  // one is open at a time — opening either sends the other to its peek —
  // which is Live's Clip View and Browser sharing a screen that has room for
  // one. While a row is carried off the shelf it drops to peek and comes back.
  const materialSheet = useSheetSnap("peek");
  const { hide: hideMaterial } = materialSheet;
  const { hide: hideEditor } = editorSheet;
  useEffect(() => {
    if (materialSheet.open) hideEditor();
  }, [materialSheet.snap, materialSheet.open, hideEditor]);
  useEffect(() => {
    if (editorSheet.open) hideMaterial();
  }, [editorSheet.snap, editorSheet.open, hideMaterial]);
  const snapBeforeLift = useRef<SheetSnap | null>(null);
  // The shelf must stay MOUNTED while a row is carried — the lift lives in
  // that row, and unmounting it cancels the drag — so at peek-while-lifting
  // the content is kept and hidden rather than dropped.
  const [shelfLifting, setShelfLifting] = useState(false);
  const { snap: materialSnap, setSnap: setMaterialSnap } = materialSheet;
  const onShelfLifting = useCallback(
    (lifting: boolean) => {
      setShelfLifting(lifting);
      if (lifting) {
        snapBeforeLift.current = materialSnap;
        setMaterialSnap("peek");
      } else if (snapBeforeLift.current) {
        setMaterialSnap(snapBeforeLift.current);
        snapBeforeLift.current = null;
      }
    },
    [materialSnap, setMaterialSnap],
  );
  const shelfOpen = narrow ? materialSheet.open : libraryOpen;
  const toggleShelf = useCallback(() => {
    if (narrow) materialSheet.toggle();
    else setLibraryOpen((v) => !v);
  }, [narrow, materialSheet]);
  const shelfHandle = narrow ? materialSheet.handle : {};
  /** The room's ⌘F: the shelf open, its search box focused. */
  const focusMaterialSearch = useCallback(() => {
    if (narrow) materialSheet.show();
    else setLibraryOpen(true);
    requestAnimationFrame(() => document.getElementById(MATERIAL_SEARCH_ID)?.focus());
  }, [narrow, materialSheet]);
  const launchedCount = Object.keys(player.sessionPlaying).length;

  // Which lane the phone's mixer strip is showing. A pointer gets the mixer
  // tab for this; a thumb gets one lane at a time, off the lane header it
  // just tapped.
  const [selectedLaneId, setSelectedLaneId] = useState<string | null>(null);

  // Device chains (Live's device view, along the bottom): the selected
  // lane's chain, its knobs moving Live's, and every lane's chain heard
  // through the browser's stand-ins (lib/device-graph.ts) unless the
  // listener prefers it dry. A knob moving is heard before it's proposed —
  // its lane plays the drafted chain until the knob lets go.
  // A Live Set in the room: an extension on the roster. A browser room has no roster and no Set.
  const liveInRoom = presence?.participants?.some((p) => (p.kind ?? "extension") === "extension") ?? false;
  const [hearDevices, setHearDevices] = usePref<boolean>("room:hearDevices", true, isBoolean);
  const [deviceViewOpen, setDeviceViewOpen] = useState(false);
  const [devicePreview, setDevicePreview] = useState<{ laneId: string; devices: DeviceState[] } | null>(null);
  const devicesByLane = useMemo(() => {
    const out: Record<string, DeviceState[]> = {};
    for (const t of room?.tracks ?? []) if (t.devices) out[t.id] = t.devices;
    return out;
  }, [room?.tracks]);
  const ownedChains = useMemo(() => {
    const out: Record<string, DeviceState[]> = {};
    for (const [owner, chain] of Object.entries(room?.chains ?? {})) if (chain.rev > 0) out[owner] = chain.devices;
    return out;
  }, [room?.chains]);
  const { setLaneDevices, setMasterDevices, setReturnDevices, setHearDevices: setPlayerHearDevices } = player;
  useEffect(() => setPlayerHearDevices(hearDevices), [setPlayerHearDevices, hearDevices]);
  useEffect(() => {
    for (const t of room?.tracks ?? []) setLaneDevices(t.id, devicePreview?.laneId === t.id ? devicePreview.devices : devicesByLane[t.id]);
  }, [setLaneDevices, devicesByLane, devicePreview, room?.tracks]);
  useEffect(() => {
    setMasterDevices(ownedChains["master"]);
    for (const owner of Object.keys(ownedChains)) {
      const m = /^return:(\d+)$/.exec(owner);
      if (m) setReturnDevices(Number(m[1]), ownedChains[owner]);
    }
  }, [setMasterDevices, setReturnDevices, ownedChains]);
  const previewLaneId = selectedLaneId;
  const onDevicePreview = useCallback(
    (devices: DeviceState[] | null) => setDevicePreview(devices && previewLaneId ? { laneId: previewLaneId, devices } : null),
    [previewLaneId],
  );
  const onDeviceParam = useCallback(
    (device: number, param: number, value: number) => {
      const { room: now, editor: ed } = landing.current;
      if (!now || !selectedLaneId) return;
      const intent = setDeviceParam(now, selectedLaneId, device, param, value);
      if (intent) ed.propose([intent]);
    },
    [selectedLaneId],
  );
  const onDeviceInsert = useCallback(
    (index: number, name: string) => {
      const { room: now, editor: ed } = landing.current;
      if (!now || !selectedLaneId) return;
      // Nobody to fill it in (no Live in the room): insert it filled in with
      // Live's stock parameters, editable and heard at once. With a Set
      // here, a placeholder its extension turns into the real device.
      const intent = insertDevice(now, selectedLaneId, index, name, liveInRoom ? undefined : (stockDevice(name) ?? undefined));
      if (intent) ed.propose([intent]);
    },
    [selectedLaneId, liveInRoom],
  );
  const onDeviceRemove = useCallback(
    (index: number) => {
      const { room: now, editor: ed } = landing.current;
      if (!now || !selectedLaneId) return;
      const intent = removeDevice(now, selectedLaneId, index);
      if (intent) ed.propose([intent]);
    },
    [selectedLaneId],
  );
  // Picking a track shows its devices, as clicking a track's title does in
  // Live — on a desktop. A phone opens them from the track's strip.
  useEffect(() => {
    if (selectedLaneId && !narrow) setDeviceViewOpen(true);
  }, [selectedLaneId, narrow]);
  const rec = useRoomRecorder({ room, player, tempo, editor, store: takes, selectedLaneId });

  // "Mine" on the shelf: the producer's clip library, placed into this room.
  const mine = useMineShelf({ room, editor, tempo, jamId, seedId: seedId || undefined, grid, playheadBeats, selectedLaneId });
  const { onDropMine, onDropMineMidi, onDropMineMidiNewLane } = mine;

  // Audio files dragged in from the desktop (or anywhere): onto a lane, or
  // anywhere else in the room for a track each. Kept like recorded takes.
  const fileDrop = useAudioFileDrop({ room, editor, takes, tempo, grid });
  const { dropOnLane: onDropFiles } = fileDrop;

  // The clip editor (Live's Clip View): one clip at a time — the audio clip
  // editor, or the piano roll for a MIDI clip. A press loads a clip into it
  // minimized; a double-click, a double-tap or a hold opens it (`editorSheet`
  // above) — docked at the bottom on a wide screen, a sheet on a phone. It
  // follows the room — a clip a peer deletes, or an undo takes away, closes it.
  const [editingClipId, setEditingClipId] = useState<string | null>(null);

  // New tracks and clips (GarageBand's way: an instrument comes with an
  // empty clip, open in the piano roll), landing at the insert marker.
  // A seed made from nothing opens on the New track sheet: picking the first
  // sound is the only thing to do there.
  const [addTrackOpen, setAddTrackOpen] = useState(() => newSeed !== undefined && (room?.tracks.length ?? 0) === 0);
  const insertNowRef = useRef(0);
  insertNowRef.current = (player.insertSec * tempo) / 60;
  const readInsertNow = useCallback(() => insertNowRef.current, []);
  const creation = useTrackCreation({
    room,
    editor,
    player,
    insertBeats: readInsertNow,
    // A new instrument's empty clip is there to be drawn in: open.
    onOpen: openEditor,
    // Selected, the new lane is where the record button and the shelf's ＋ land.
    onAudioLane: setSelectedLaneId,
  });
  const { addMidiClip } = creation;
  const addInstrumentTrack = useCallback(
    (id: InstrumentId) => {
      // The new clip opens on the timeline, where it sits.
      if (creation.addInstrument(id)) setView("timeline");
    },
    [creation],
  );

  const edit = useMemo(
    () =>
      editState && !listening
        ? {
            ...editState,
            onDropMine,
            onDropMineMidi,
            onDropFiles,
            onCreateMidiClip: (laneId: string, atBeat: number) => void addMidiClip(laneId, atBeat),
            onAddMidiClip: (laneId: string) => void addMidiClip(laneId),
          }
        : undefined,
    [editState, onDropMine, onDropMineMidi, onDropFiles, listening, addMidiClip],
  );

  // Live's insert marker, read when an operation runs: where a paste lands
  // and a split cuts. A ref, not a value: it moves, and the operations and
  // key listeners would otherwise be rebuilt every time it did.
  const insertMarkerRef = useRef(0);
  insertMarkerRef.current = (player.insertSec * tempo) / 60;
  const readInsertMarker = useCallback(() => insertMarkerRef.current, []);

  const { setInsert: setInsertSec } = player;
  const setInsertBeats = useCallback(
    (beats: number) => setInsertSec(beatsToSeconds(Math.max(0, beats), tempoRef.current)),
    [setInsertSec],
  );
  const ops = useClipOps({
    room,
    selected,
    setSelected,
    editor,
    grid,
    tempo,
    insertBeats: readInsertMarker,
    setInsertBeats,
    land: proposeOverwrite,
    fallbackLaneId: selectedLaneId,
    timeSelection,
    setTimeSelection,
    cursorLaneId,
  });

  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const editing = useMemo(() => {
    if (!editingClipId || !arrangement) return null;
    for (const [i, lane] of arrangement.lanes.entries()) {
      const clip = lane.clips.find((c) => c.id === editingClipId) ?? lane.sessionClips.find((c) => c.id === editingClipId);
      if (clip && clip.kind !== "midi") return { clip, lane, color: clipColor(clip.color, i) };
    }
    return null;
  }, [editingClipId, arrangement]);
  useEffect(() => {
    if (editingClipId && room && !room.clips.some((c) => c.id === editingClipId)) setEditingClipId(null);
  }, [editingClipId, room]);
  // The Set's scale, as the piano roll tints its rows by (when Live has said).
  const roomScale = useMemo(() => {
    const sc = room?.scale;
    if (!sc || (room?.scaleRev ?? 0) <= 0 || sc.intervals.length === 0) return undefined;
    return { root: sc.rootNote, name: sc.name, mode: sc.mode, intervals: sc.intervals };
  }, [room]);
  // One note, heard through its lane — what drawing or picking a note
  // sounds. Polyphonic (a chord is every key held) and beside, never in the
  // way of, a clip auditioning; the piano roll releases it at key up.
  const auditionNote = useCallback(
    (laneId: string, note: WireNote): (() => void) => {
      const duration = Math.min(note.duration, 4);
      return player.auditionNote(laneId, {
        pitch: note.pitch,
        startSec: 0,
        durationSec: beatsToSeconds(duration, tempo),
        velocity: note.velocity,
      });
    },
    [tempo, player],
  );
  // The same, for a MIDI clip: the piano roll.
  const midiEditing = useMemo(() => {
    if (!editingClipId || !arrangement) return null;
    for (const [i, lane] of arrangement.lanes.entries()) {
      const clip = lane.clips.find((c) => c.id === editingClipId) ?? lane.sessionClips.find((c) => c.id === editingClipId);
      if (clip && clip.kind === "midi") return { clip, lane, color: clipColor(clip.color, i) };
    }
    return null;
  }, [editingClipId, arrangement]);
  // The other MIDI lanes' notes under the open clip — the roll's ghosts.
  const midiGhosts = useMemo(
    () => (midiEditing && arrangement ? ghostNotesFor(arrangement, midiEditing.clip, midiEditing.lane.id) : []),
    [midiEditing, arrangement],
  );
  // A launched pad's playhead is read every frame only while its roll is open.
  const { setSlotFollow } = player;
  const followSlot = midiEditing?.clip.slot !== undefined;
  useEffect(() => {
    setSlotFollow(followSlot);
    return () => setSlotFollow(false);
  }, [followSlot, setSlotFollow]);

  // The pads' editing: hold a pad (or flip Edit) to select it, then act on it
  // from the bar under the grid — the timeline's operations, in slots.
  const [padEditMode, setPadEditMode] = useState(false);
  const insertRef = useRef(0);
  insertRef.current = (player.insertSec * tempo) / 60;
  const readInsert = useCallback(() => insertRef.current, []);
  const padOps = usePadOps({
    room,
    editor,
    sceneCount: rawArrangement?.sceneCount ?? 0,
    grid: GRID,
    insertBeats: readInsert,
    onOpen: openEditor,
  });
  // A wide screen loads whichever pad was pressed into the editor's strip; a
  // phone keeps a tap for launching. A hold opens it on either.
  // A pad opens in the editor: a scene pad as itself, a timeline pad as the
  // timeline clip it mirrors — no copy made on the way in. (Live: a click on
  // a Session or Arrangement clip shows that clip; a pad here that is drawn
  // from the timeline IS that clip.)
  // A press loads it minimized; only a hold opens it (usePadOps' `hold`). A
  // press also launches the pad, so a double-click would launch it twice.
  const onPadPress = useCallback((clipId: string) => {
    const sourceId = ghostSource(clipId) ?? clipId;
    if (roomRef.current?.clips.some((x) => x.id === sourceId)) setEditingClipId(sourceId);
  }, []);
  // Material off the shelf onto an empty pad: a copy of a piece of this
  // project, or one of your clips placed there.
  const { onDropMineSlot } = mine;
  const dropToSlot = useCallback(
    (laneId: string, row: number, payload: MaterialPayload) => {
      if ("mine" in payload) {
        onDropMineSlot(laneId, row, payload.mine);
        return;
      }
      if ("mineMidi" in payload || "reference" in payload) return; // pads take audio from Mine for now
      const now = roomRef.current;
      const intent = now ? copyClipToSlot(now, payload.material, laneId, row) : null;
      if (intent) editor.propose([intent]);
    },
    [onDropMineSlot, editor],
  );
  const padEdit: PadEditState = useMemo(
    () => ({
      ops: padOps,
      editMode: padEditMode,
      onEditMode: setPadEditMode,
      onPadPress: narrow ? undefined : onPadPress,
      onDropToSlot: dropToSlot,
    }),
    [padOps, padEditMode, narrow, onPadPress, dropToSlot],
  );
  // Opening a timeline clip puts the insert marker on its start, so Space
  // plays the clip being edited, in the arrangement.
  // Unless the marker is already inside it (a click placed it there).
  // A press on the timeline opens the clip AND places the marker where the
  // finger landed; that marker is the point ⌘E splits at and Space plays
  // from, so an open that came from a press leaves it alone. Opens from
  // elsewhere — a pad, a new clip, a hold — still bring the marker to the
  // clip, so Space plays what is being edited.
  const editingStart = editing && editing.clip.slot === undefined ? editing.clip.start : null;
  const editingEnd = editing && editing.clip.slot === undefined ? editing.clip.start + editing.clip.duration : null;
  useEffect(() => {
    if (editingClipId) trackAnalytics("browser_editor_opened");
    const byPress = openedByPress.current;
    openedByPress.current = false;
    if (byPress) return;
    const at = (player.insertSec * tempoRef.current) / 60;
    const inside = editingStart !== null && editingEnd !== null && at >= editingStart && at < editingEnd;
    if (editingStart !== null && !inside) setInsert(beatsToSeconds(editingStart, tempoRef.current));
    // Only when a clip is opened, not every time its start is nudged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingClipId]);
  // Where the transport is inside the clip being edited, in clip beats.
  const editingPlayhead = useMemo(() => {
    if (!editing || editing.clip.slot !== undefined || player.status !== "playing") return null;
    const rel = playheadBeats - editing.clip.start;
    const g = editing.clip.geometry;
    if (!g) return rel >= 0 && rel <= editing.clip.duration ? rel : null;
    return objectPositionAt({ ...g, duration: editing.clip.duration }, rel);
  }, [editing, playheadBeats, player.status]);
  // Live's Consolidate (⌘J): each lane's part of the selection (a time span,
  // or the selected clips' extent) becomes one clip over the whole span. MIDI
  // merges its notes as they play; audio is rendered as it sounds, dry, and
  // kept like a take. Everything lands as one gesture, against the room as it
  // stands after the renders (lib/consolidate.ts).
  const [consolidating, setConsolidating] = useState(false);
  const consolidate = useCallback(async () => {
    const start = roomRef.current;
    if (!start || !arrangement || consolidating) return;
    const span = consolidateTarget(start, timeSelection, ops.selection);
    if (!consolidatable(span)) return;
    const bpm = tempoRef.current;
    const plan = planConsolidate(start, span, bpm);
    if (plan.lanes.length === 0) {
      setEditNote(plan.refused.length > 0 ? "Those clips have nothing to consolidate." : "Nothing in the selection to consolidate.");
      return;
    }
    setConsolidating(true);
    try {
      const creates: Intent[] = [];
      let refused = plan.refused.length;
      const fromSec = beatsToSeconds(span.start, bpm);
      const toSec = beatsToSeconds(span.end, bpm);
      for (const lane of plan.lanes) {
        if (lane.kind === "midi") {
          const made = consolidatedMidiClip(lane, span);
          if (made) creates.push(made);
          else refused += 1;
          continue;
        }
        // The lane's clips as the transport plays them, rendered from the
        // earliest one's start (a clip that began before the span still
        // sounds into it) and cut to the span.
        const laneModel = arrangement.lanes.find((l) => l.id === lane.laneId);
        const ids = new Set(clipsInSpan(start, { ...span, laneIds: [lane.laneId] }).map((c) => c.id));
        if (!laneModel) continue;
        const sources = roomSourcesWithUrls(
          { ...arrangement, lanes: [{ ...laneModel, clips: laneModel.clips.filter((c) => ids.has(c.id)) }] },
          bpm,
          urlOfKey,
        );
        const renderFrom = Math.max(0, Math.min(fromSec, ...sources.map((src) => src.startSec)));
        setEditNote(`Consolidating ${laneModel.name || "a lane"}…`);
        const rendered = await player.renderSources(
          sources.map((src) => ({ ...src, startSec: src.startSec - renderFrom })),
          toSec - renderFrom,
        );
        const key = await takes.put(encodeWav24(rendered, fromSec - renderFrom, toSec - renderFrom));
        const made = consolidatedAudioClip(lane, span, key, bpm);
        if (made) creates.push(made);
      }
      const now = roomRef.current;
      if (!now) return;
      const intents = consolidateIntents(now, span, creates, bpm);
      if (intents === null) {
        setEditNote("Something in the way can't be cut there: a transposed unwarped clip only cuts once it's warped.");
        return;
      }
      if (intents.length === 0) {
        setEditNote("Nothing in the selection to consolidate.");
        return;
      }
      editor.propose(intents);
      setSelected(new Set());
      setTimeSelection(span);
      setEditNote(
        refused > 0
          ? `Consolidated ${creates.length} lane${creates.length === 1 ? "" : "s"} — ${refused} couldn't be (no audio, or too many notes).`
          : null,
      );
    } catch (e: unknown) {
      setEditNote(`Could not consolidate: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setConsolidating(false);
    }
  }, [arrangement, consolidating, timeSelection, ops.selection, player, urlOfKey, takes, editor]);

  // "Bounce in place": the clip rendered as it sounds (warp, pitch, loop;
  // dry), kept like a recorded take (uploaded in a connected room, held in
  // the tab in a local one). It replaces the clip's own audio, and the
  // original is kept as a backup in the Session View's first empty row. Read the room fresh after
  // the awaits: a peer may have changed it meanwhile.
  const bounceClip = useCallback(async (clipId: string): Promise<string> => {
    const found = findEditableAudio(arrangementRef.current, clipId);
    if (!found) throw new Error("No clip is open.");
    const { clip, lane } = found;
    const url = clip.audioKey ? urlOfKey(clip.audioKey) : undefined;
    if (!url) throw new Error("This clip has no audio to bounce.");
    const rendered = await player.renderSources(clipSourcesFromStart(lane, clip, url, tempo), beatsToSeconds(clip.duration, tempo));
    const key = await takes.put(encodeWav24(rendered));
    const now = roomRef.current;
    if (!now?.clips.some((c) => c.id === clip.id)) throw new Error("The clip went away while it was bouncing.");
    const intents = bounceInPlace(now, clip.id, key, tempo);
    if (intents.length === 0) throw new Error("Couldn't bounce: the Session View has no empty row left.");
    editor.propose(intents);
    const copy = intents[1];
    return copy?.kind === "upsertClip" && copy.clip.slot !== undefined
      ? `Bounced in place. The original is kept in the Session View, row ${copy.clip.slot + 1}.`
      : "Bounced in place.";
  }, [urlOfKey, player, tempo, takes, editor]);
  const bounceEditing = useCallback(
    (): Promise<string> => (editingClipId ? bounceClip(editingClipId) : Promise.reject(new Error("No clip is open."))),
    [editingClipId, bounceClip],
  );

  // The render for hosts without a warp engine (ClipData.render). A warp edit
  // Live can't take leaves the clip wanting a render; once the room has been
  // quiet for a moment, THIS tab (the one that edited) renders the clip as it
  // sounds, uploads it, and records it. The original stays the canonical clip.
  // A clip edited again mid-render makes the finished one stale: it is
  // dropped and the clip is rendered again.
  const renderBusy = useRef(new Set<string>());
  const renderFailed = useRef(new Set<string>());
  const [renderKick, setRenderKick] = useState(0);
  const onWarpEdit = useCallback(() => setRenderKick((n) => n + 1), []);
  const renderClip = useCallback(
    async (clipId: string): Promise<void> => {
      const found = findEditableAudio(arrangementRef.current, clipId);
      const canon = roomRef.current?.clips.find((c) => c.id === clipId);
      const url = found?.clip.audioKey ? urlOfKey(found.clip.audioKey) : undefined;
      if (!found || !canon || !url) return;
      const hash = warpRenderHash(canon);
      const rendered = await player.renderSources(clipSourcesFromStart(found.lane, found.clip, url, tempo), beatsToSeconds(found.clip.duration, tempo));
      const key = await takes.put(encodeWav24(rendered));
      const now = roomRef.current;
      const intent = now ? completeRender(now, clipId, { key, hash, tempo }) : null;
      if (intent) editor.proposeUnrecorded([intent]);
    },
    [urlOfKey, player, tempo, takes, editor],
  );
  // Any clip left pending is rendered here, not only the ones this tab just
  // edited: a split or a copy inherits a render that is stale for its new
  // shape, and a tab closed mid-render leaves its clip waiting. Two tabs may
  // both render; the second's `completeRender` is a harmless repeat.
  // Clips from before renders existed (or from a flow that makes none) are
  // asked for once per session: the request leaves them pending, and the pass
  // below renders them.
  const renderRequested = useRef(new Set<string>());
  useEffect(() => {
    if (listening || !room) return;
    const asks: Intent[] = [];
    for (const clip of room.clips) {
      if (renderRequested.current.has(clip.id)) continue;
      const ask = requestRender(room, clip.id);
      if (!ask) continue;
      renderRequested.current.add(clip.id);
      asks.push(ask);
    }
    if (asks.length > 0) editor.proposeUnrecorded(asks);
  }, [room, listening, editor]);
  useEffect(() => {
    if (listening) return;
    const timer = setTimeout(() => {
      for (const clip of roomRef.current?.clips ?? []) {
        if (clip.clipKind === "midi" || renderStatus(clip) !== "pending") continue;
        const id = clip.id;
        const attempt = `${id}:${warpRenderHash(clip)}`;
        if (renderBusy.current.has(id) || renderFailed.current.has(attempt)) continue;
        renderBusy.current.add(id);
        renderClip(id)
          .catch((err: unknown) => {
            console.warn("[render] could not render a clip for Live", err);
            renderFailed.current.add(attempt);
          })
          .finally(() => {
            renderBusy.current.delete(id);
            setRenderKick((n) => n + 1);
          });
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [room, renderKick, renderClip, listening]);

  // The editor's ▶ is Preview: the clip heard ON ITS OWN, edits and all,
  // looping, in time with whatever runs — a timeline clip and a Session
  // clip alike (a pad's launch stays on the pad). Pressed again, it stops.
  const heard = editing ?? midiEditing;
  const { preview } = playback;
  const previewEditing = useCallback(() => {
    if (heard) preview.clip(heard.clip, heard.lane);
  }, [heard, preview]);
  const previewing = heard ? preview.isPreviewing(heard.clip.id) : false;
  // Closing the editor stops its preview. Keyed on the editor's state and
  // the stable stop verb only — never on `preview` itself, which is rebuilt
  // whenever the audition state changes: with the editor closed, that made
  // this effect fire the moment the shelf STARTED a preview, cancelling it
  // while it was still waiting on the AudioContext (2026-10-16).
  const { stop: stopPreview } = preview;
  useEffect(() => {
    if (!editingClipId && heard === null) stopPreview();
  }, [editingClipId, heard, stopPreview]);

  // Live's Clip View — one instance, docked under whichever view opened it;
  // on a phone, in the sheet under it (below).
  // `actions`: docked, the clip's action bar rides in the editor's strip
  // (the page row under the timeline steps aside while it is open).
  const renderEditorPanel = (actions?: ReactNode) => editing && room && !listening ? (
      <ClipEditor
        actions={actions}
        room={room}
        clipId={editing.clip.id}
        tempo={tempo}
        url={editing.clip.audioKey ? urlOfKey(editing.clip.audioKey) : undefined}
        color={editing.color}
        player={player}
        playheadBeat={editingPlayhead}
        propose={editor.propose}
        onClose={() => setEditingClipId(null)}
        onMinimize={narrow ? undefined : hideEditor}
        presentation={narrow ? "sheet" : "docked"}
        onPreview={previewEditing}
        previewing={previewing}
        history={{ undo: editor.undo, redo: editor.redo, canUndo: editor.canUndo, canRedo: editor.canRedo }}
        onBounce={bounceEditing}
        onWarpEdit={onWarpEdit}
        onDownload={downloads.canDownloadClip(editing.clip.id) ? () => downloads.askClip(editing.clip.id, "clip_editor") : undefined}
      />
  ) : midiEditing && room && !listening ? (
      <MidiClipEditor
        clip={midiEditing.clip}
        color={midiEditing.color}
        actions={actions}
        history={{ undo: editor.undo, redo: editor.redo, canUndo: editor.canUndo, canRedo: editor.canRedo }}
        onPreview={previewEditing}
        previewing={previewing}
        onClipFields={(fields) => {
          const now = roomRef.current;
          const intent = now ? setClipFields(now, midiEditing.clip.id, fields) : null;
          if (intent) editor.propose([intent]);
        }}
        presentation={narrow ? "sheet" : "docked"}
        kit={midiEditing.lane.instrument === "kit"}
        ghostNotes={midiGhosts}
        roomScale={roomScale}
        playheadBeats={
          midiEditing.clip.slot !== undefined
            ? player.sessionPlaying[midiEditing.lane.id] === midiEditing.clip.id
              ? (() => {
                  const sec = player.slotPositionSec(midiEditing.lane.id);
                  return sec === null ? undefined : (sec * tempo) / 60;
                })()
              : undefined
            : player.status === "playing" &&
                playheadBeats >= midiEditing.clip.start &&
                playheadBeats < midiEditing.clip.start + midiEditing.clip.duration
              ? playheadBeats - midiEditing.clip.start
              : undefined
        }
        onAudition={(note) => auditionNote(midiEditing.lane.id, note)}
        onChange={(notes) => {
          const now = roomRef.current;
          const intent = now ? setClipNotes(now, midiEditing.clip.id, notes) : null;
          if (intent) editor.propose([intent]);
        }}
        onScaleLoop={(factor) => {
          const now = roomRef.current;
          const intent = now ? scaleClipLoop(now, midiEditing.clip.id, factor, 0.25) : null;
          if (intent) proposeOverwrite([intent]);
        }}
        onClose={() => setEditingClipId(null)}
        onMinimize={narrow ? undefined : hideEditor}
      />
  ) : null;


  // Material dropped below the last lane: a copy on a new track of its own.
  const dropMaterialOnNewLane = useCallback(
    (clipId: string) => {
      const now = roomRef.current;
      if (!now) return;
      const intents = copyClipToNewLane(now, clipId, nextOrd(now), Math.max(0, insertNowRef.current));
      if (intents.length > 0) editor.propose(intents);
    },
    [editor],
  );

  // A new seed's Save. Audio the pool doesn't hold goes up as bytes: a take
  // held in this tab (an object URL), or whatever else a clip plays.
  const bytesOf = useCallback(
    async (key: string): Promise<Uint8Array | null> => {
      const url = localAudioUrl(key);
      if (!url) return null;
      try {
        const res = await fetch(url);
        return res.ok ? new Uint8Array(await res.arrayBuffer()) : null;
      } catch {
        return null;
      }
    },
    [localAudioUrl],
  );
  const saveNewSeed = (controls: NewSeedControls) =>
    room ? (
      <SaveNewSeed
        title={title}
        onTitle={controls.onTitle}
        room={room}
        tempo={tempo}
        bytesOf={bytesOf}
        renderMaster={renderMaster}
        onSaved={(id) => {
          editor.markClean();
          controls.onSaved(id);
        }}
      />
    ) : null;

  // A room with nothing in it yet: the first track is the next thing to do.
  const emptyRoomPrompt = (
    <div className="p-4 wide:p-6">
      <div className="mx-auto flex max-w-[520px] flex-col items-center gap-4 rounded-2xl border border-dashed border-panelline p-8 text-center wide:p-10">
        <p className="text-sm text-muted-foreground">Nothing here yet.</p>
        <Button onClick={() => setAddTrackOpen(true)}>
          <Plus className="size-4" />
          New track
        </Button>
      </div>
    </div>
  );

  const addTrackSheet = listening ? null : (
    <AddTrackSheet
      open={addTrackOpen}
      onOpenChange={setAddTrackOpen}
      narrow={narrow}
      onInstrument={addInstrumentTrack}
      onRecord={() => {
        if (creation.addAudio()) setView("timeline");
      }}
      onLoops={() => {
        if (!creation.addAudio()) return;
        setView("timeline");
        mine.setFilter("mine");
        setLibraryOpen(true);
      }}
      onPreview={creation.preview}
      previewing={creation.previewing}
    />
  );

  // The Clip View follows the clip you press (`onSelect` above; Live's
  // habit) — but minimized, as a strip under the timeline or the pads, until
  // a double-click or a hold opens it (`openEditor`). Pressing another clip
  // swaps what is in it at whatever size it is; clearing the selection
  // leaves the last one there, as Live does, until it is closed. A closed
  // editor goes back to minimized, so the next clip starts small.
  useEffect(() => {
    if (!editingClipId) hideEditor();
  }, [editingClipId, hideEditor]);

  // Every clip on the timeline — what ⌘A selects. The Session View's clips are
  // deliberately not in it: they have no timeline position, so nothing here can
  // act on them.
  const allClipIds = useMemo(
    () => (arrangement ? arrangement.lanes.flatMap((l) => l.clips.map((c) => c.id)) : []),
    [arrangement],
  );

  // What the key handlers read. A ref rather than an effect dependency list, so
  // the listeners are registered once instead of on every frame the transport
  // moves.
  // Live's Loop Selection (⌘L): the transport loops the selection's span; a
  // second press on the same span turns the loop off.
  const { setLoopRange } = player;
  const loopSelection = useCallback(() => {
    const sel = ops.selection.filter((c) => c.slot === undefined);
    if (sel.length === 0 && !ops.span) return;
    const from = ops.span ? ops.span.start : Math.min(...sel.map((c) => c.startTime));
    const to = ops.span ? ops.span.end : Math.max(...sel.map((c) => c.startTime + c.duration));
    const range = { startSec: beatsToSeconds(from, tempo), endSec: beatsToSeconds(to, tempo) };
    const same = player.loopRange && Math.abs(player.loopRange.startSec - range.startSec) < 1e-3 && Math.abs(player.loopRange.endSec - range.endSec) < 1e-3;
    setLoopRange(same ? null : range);
  }, [ops.selection, ops.span, tempo, player.loopRange, setLoopRange]);

  // A right-click on the timeline: the same verbs as the action bar and the
  // keys, offered by what is under the pointer (lib/arrangement-menu.ts).
  // Asked as the menu draws, so it reads the selection the click just made.
  const arrangementContextMenu = (target: ArrangementMenuTarget) =>
    arrangementMenu(target, {
      ops: {
        can: ops.can,
        muted: ops.muted,
        selectionSize: ops.selection.length,
        clipboardSize: ops.clipboardSize,
        copy: ops.copy,
        cut: ops.cut,
        paste: ops.paste,
        duplicate: ops.duplicate,
        split: ops.split,
        remove: ops.remove,
        toggleMute: ops.toggleMute,
      },
      loopSelection,
      consolidate: () => void consolidate(),
      consolidating,
      openEditor,
      createMidiClip: (laneId, atBeat) => void addMidiClip(laneId, atBeat),
      addMidiClip: (laneId) => void addMidiClip(laneId),
      selectClips: (clipIds) => {
        setTimeSelection(null);
        onSelectMany(clipIds, false);
      },
      trackFlag: (laneId, field, value) => edit?.onTrackField(laneId, field, value),
      deleteTrack: edit ? (laneId) => edit.onDeleteTrack(laneId) : undefined,
      backToArrangement: audio.onBackToArrangement,
      overridden: audio.overridden,
      playFrom: (beat) => player.jumpTo(beatsToSeconds(Math.max(0, beat), tempo)),
      setInsert: setInsertBeats,
      loop: { on: player.loop, toggle: () => player.setLoop(!player.loop) },
      // The ruler the view draws is 4/4 here (no meter is passed to it).
      formatBeat: (beat) => barsBeats(beat, FOUR_FOUR),
    });

  const live = useRef({ ops, player, allClipIds, onSelectMany, editor, shortcutsOpen, loopSelection, grid, consolidate });
  live.current = { ops, player, allClipIds, onSelectMany, editor, shortcutsOpen, loopSelection, grid, consolidate };

  // Transport keys, wherever you are on the page. Space is ALWAYS the
  // transport's (the clip editor included) unless it is being typed, and
  // it plays what the room holds now, edits and all. Listened for in the
  // capture phase and stopped there, so a focused button never takes it
  // as a click as well: a button activates on Space's keyup, which is
  // swallowed too. Home / Enter keep their narrower guards.
  useEffect(() => {
    // By code as well as key: ⌥Space on a Mac types a non-breaking space.
    const spaceIsOurs = (e: KeyboardEvent) =>
      (e.code === "Space" || e.key === " ") && !e.metaKey && !e.ctrlKey && !textEntry(e.target) && (!inModal(e.target) || inTransportModal(e.target));
    const onKeyDown = (e: KeyboardEvent) => {
      const { player: p, ops: o } = live.current;
      if (spaceIsOurs(e)) {
        e.preventDefault();
        e.stopPropagation();
        if (e.repeat) return;
        stopExternalPreviews();
        p.stopAudition();
        if (e.shiftKey) {
          // Live's ⇧Space: carry on from where playback stopped.
          p.resumeFromStop();
        } else if (e.altKey) {
          // Live's ⌥Space: the selection, once, then back to the marker.
          const start = o.span?.start ?? o.selection.reduce((min, c) => Math.min(min, c.startTime), Infinity);
          const end = o.span?.end ?? o.selection.reduce((max, c) => Math.max(max, c.startTime + c.duration), -Infinity);
          if (Number.isFinite(start) && end > start) {
            p.playRange(beatsToSeconds(start, tempoRef.current), beatsToSeconds(end, tempoRef.current));
          } else {
            p.playFromInsert();
          }
        } else {
          p.playFromInsert();
        }
        return;
      }
      if (typingTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const control = (e.target as HTMLElement | null)?.closest?.("button, a");
      if (control && !control.hasAttribute("data-clip-id")) return;
      if (e.key === "Home" || (e.key === "Enter" && !e.shiftKey)) {
        e.preventDefault();
        p.returnToStart();
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (!spaceIsOurs(e)) return;
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("keyup", onKeyUp, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("keyup", onKeyUp, true);
    };
  }, []);

  // Undo / redo on the Pads screen too: its edits are the room's like any
  // other (the grid's own keys are SessionPads').
  useEffect(() => {
    if (view !== "pads") return;
    const onKey = (e: KeyboardEvent) => {
      if (typingTarget(e.target) || inModal(e.target)) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      const key = e.key.toLowerCase();
      const ed = live.current.editor;
      if (key === "z") {
        e.preventDefault();
        if (e.shiftKey) ed.redo();
        else ed.undo();
      } else if (key === "y") {
        e.preventDefault();
        ed.redo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view]);

  // Editing keys, only while the timeline is the view they would act on.
  useEffect(() => {
    if (view !== "timeline") return;
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      // Typing owns its keys. The clip editor is a dialog too, but a ⌘-key
      // pressed in it (not in one of its fields) is the room's — ⌘E splits
      // the clip you are looking at.
      if (textEntry(e.target) || (typingTarget(e.target) && !(mod && inTransportModal(e.target)))) return;
      const { ops: o, allClipIds: ids, onSelectMany: selectMany, editor: ed, shortcutsOpen: sheet } = live.current;
      const key = e.key.toLowerCase();
      if (mod && key === "z") {
        e.preventDefault();
        if (e.shiftKey) ed.redo();
        else ed.undo();
      } else if (mod && key === "y") {
        e.preventDefault();
        ed.redo();
      } else if (mod && key === "f") {
        // Live's Search in Browser: the shelf, its search box.
        e.preventDefault();
        focusMaterialSearch();
      } else if (mod && key === "a") {
        e.preventDefault();
        setTimeSelection(null);
        selectMany(ids, false);
      } else if (mod && (key === "1" || key === "2")) {
        // Live's narrower / wider grid.
        e.preventDefault();
        setGridDensity((d) => Math.max(GRID_DENSITY_MIN, Math.min(GRID_DENSITY_MAX, key === "1" ? d * 2 : d / 2)));
      } else if (mod && key === "4") {
        // Live's snap to grid, on / off.
        e.preventDefault();
        setSnapOn((on) => !on);
      } else if (mod && key === "c") {
        // No preventDefault: the browser's own copy is welcome to run too.
        o.copy();
      } else if (mod && key === "x") {
        e.preventDefault();
        o.cut();
      } else if (mod && key === "v") {
        e.preventDefault();
        o.paste();
      } else if (mod && key === "d") {
        e.preventDefault();
        o.duplicate();
      } else if (mod && key === "e") {
        // Live's Split: the selection cut at the insert marker.
        e.preventDefault();
        o.split();
      } else if (mod && key === "j") {
        // Live's Consolidate: each lane's part of the selection as one clip.
        e.preventDefault();
        void live.current.consolidate();
      } else if (mod && key === "l") {
        // Live's Loop Selection: the transport loops the selected span.
        if (o.selection.length === 0 && !o.span) return;
        e.preventDefault();
        live.current.loopSelection();
      } else if (mod) {
        // Every other modified key belongs to the browser or the platform, and
        // must not fall through: ⌘M would otherwise mute, ⌘← would nudge.
        return;
      } else if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        o.remove();
      } else if ((e.key === "ArrowLeft" || e.key === "ArrowRight") && e.shiftKey && o.span) {
        // Live's ⇧← / ⇧→: the time selection's end shorter / longer by the grid.
        e.preventDefault();
        const step = live.current.grid > 0 ? live.current.grid : 1 / 16;
        const end = o.span.end + (e.key === "ArrowLeft" ? -step : step);
        if (end - o.span.start > 1e-9) setTimeSelection({ ...o.span, end });
      } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        const dir = e.key === "ArrowLeft" ? -1 : 1;
        if (o.selection.length === 0) {
          // A span with no clips in it: the selection itself moves along.
          if (!o.span) return;
          e.preventDefault();
          const step = live.current.grid > 0 ? live.current.grid : 1 / 16;
          const by = Math.max(dir * step, -o.span.start);
          setTimeSelection({ ...o.span, start: o.span.start + by, end: o.span.end + by });
          return;
        }
        e.preventDefault();
        o.nudge(dir * (e.shiftKey ? 4 : 1));
      } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        if (o.selection.length === 0) return;
        e.preventDefault();
        o.moveLane(e.key === "ArrowUp" ? -1 : 1);
      } else if (key === "m") {
        e.preventDefault();
        o.toggleMute();
      } else if (e.key === "?" || (e.shiftKey && e.key === "/")) {
        e.preventDefault();
        setShortcutsOpen((open) => !open);
      } else if (e.key === "Escape") {
        if (sheet) {
          setShortcutsOpen(false);
          return;
        }
        setSelected(new Set());
        setTimeSelection(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view]);

  // Nothing here is saved until it is published, and a room keeps no history:
  // the tab closing, the Back button and the back link below all ask first.
  // A new seed's room is kept as a draft as it changes (BlankRoomPage):
  // leaving loses nothing, so there is nothing to ask.
  const guard = useUnsavedGuard(
    editor.dirty && !newSeed,
    "This room has changes nobody has saved. Leave and lose them?",
  );


  // Files dragged over the room: the outline says where they can go. Lanes,
  // the New track row and the shelf take their own drops first (they call
  // preventDefault); whatever reaches the window becomes new tracks — the
  // New track sheet included, which sits outside the room's box — and is
  // never left to the browser, which would open the file and leave the room.
  const [fileDragOver, setFileDragOver] = useState(false);
  const { dropAnywhere } = fileDrop;
  useEffect(() => {
    if (listening) return;
    let depth = 0;
    const hasFiles = (e: globalThis.DragEvent) => e.dataTransfer?.types.includes("Files") ?? false;
    const enter = (e: globalThis.DragEvent) => {
      if (!hasFiles(e)) return;
      depth += 1;
      setFileDragOver(true);
    };
    const leave = (e: globalThis.DragEvent) => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setFileDragOver(false);
    };
    const over = (e: globalThis.DragEvent) => {
      if (!hasFiles(e) || e.defaultPrevented) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    };
    const drop = (e: globalThis.DragEvent) => {
      depth = 0;
      setFileDragOver(false);
      // Taken already by a lane, the New track row or the shelf.
      if (!hasFiles(e) || e.defaultPrevented || !e.dataTransfer) return;
      e.preventDefault();
      setAddTrackOpen(false);
      dropAnywhere(e.dataTransfer, insertNowRef.current);
    };
    window.addEventListener("dragenter", enter);
    window.addEventListener("dragleave", leave);
    window.addEventListener("dragover", over);
    window.addEventListener("drop", drop);
    return () => {
      window.removeEventListener("dragenter", enter);
      window.removeEventListener("dragleave", leave);
      window.removeEventListener("dragover", over);
      window.removeEventListener("drop", drop);
    };
  }, [listening, dropAnywhere]);

  if (!arrangement || !room) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 text-muted-foreground">
        <p className="text-sm">This seed has no arrangement to remix.</p>
        <Button asChild variant="outline" size="sm">
          <Link to={backTo}>Back to seed</Link>
        </Button>
      </div>
    );
  }

  // The shelf's ＋ on the Pads view lands in a pad, not on the timeline: the
  // empty slot you picked, else the first free one on the clip's own track
  // (yours: the selected audio track, or one named after the clip).
  const emptyRowOn = (laneId: string): number | null => {
    for (let row = 0; row < MAX_SCENES; row++) {
      if (!room.clips.some((c) => c.trackId === laneId && c.slot === row)) return row;
    }
    return null;
  };
  const kindOf = (laneId: string) => room.tracks.find((t) => t.id === laneId)?.trackKind ?? "audio";
  const pickedSlot = (kind: "audio" | "midi") => {
    const sel = padOps.selection;
    return sel?.kind === "slot" && kindOf(sel.laneId) === kind ? { laneId: sel.laneId, row: sel.row } : null;
  };
  const placeMaterialInPads = (clipId: string) => {
    const source = room.clips.find((c) => c.id === clipId);
    if (!source) return;
    const kind = source.clipKind === "midi" ? "midi" : "audio";
    const row = emptyRowOn(source.trackId);
    const at = pickedSlot(kind) ?? (row === null ? null : { laneId: source.trackId, row });
    const intent = at ? copyClipToSlot(room, clipId, at.laneId, at.row) : null;
    if (intent) editor.propose([intent]);
  };
  const placeMineInPads = (clip: MyClip) => {
    const picked = pickedSlot("audio");
    if (picked) {
      void mine.placeInSlot(clip, picked.laneId, picked.row);
      return;
    }
    const lane =
      room.tracks.find((t) => t.id === selectedLaneId && kindOf(t.id) === "audio") ??
      room.tracks.find((t) => kindOf(t.id) === "audio" && displayNameOf(t.name) === clip.name);
    const row = lane ? emptyRowOn(lane.id) : null;
    // Nowhere to put a pad: onto the timeline as before (a new track).
    if (!lane || row === null) mine.shelf.onPlace(clip);
    else void mine.placeInSlot(clip, lane.id, row);
  };

  // The strip that is the whole editor while it is minimized: the clip's
  // colour and name, play, ✕. It flashes as a press loads a clip into it, so
  // a click that didn't open anything still shows where the clip went. On a
  // phone it is the sheet's handle, and the editor itself fills half and
  // full; on a wide screen a click on it docks the editor, whose own header
  // folds it back down.
  const editingClip = editing?.clip ?? midiEditing?.clip ?? null;
  const editingColor = editing?.color ?? midiEditing?.color ?? "#888";
  // What the strip says about it: what the editor's own strip says (its
  // length, and its notes or whether it follows the beat) — the chevron
  // already says it opens.
  // What the clip plays as, said plainly: a MIDI clip's bounce, its notes,
  // or why it is silent — never a note count over a clip that plays none.
  const editingMeta = editingClip
    ? `${lengthLabel(editingClip.duration)} · ${
        editingClip.kind === "midi"
          ? soundingLabel(editingClip)
          : room?.clips.find((c) => c.id === editingClip.id)?.isWarped
            ? "warped"
            : "unwarped"
      }`
    : "";
  // A phone on its side, the sheet at its peek: the clip's actions share the
  // name's row. Stacked, the two rows left one lane of the timeline in view.
  const inlineActions = short && !editorSheet.open;
  const makeActionBar = (dense: boolean) =>
    view === "timeline" && (ops.selection.length > 0 || ops.span || ops.clipboardSize > 0) ? (
      <ClipActionBar
        can={ops.can}
        muted={ops.muted}
        onCopy={ops.copy}
        onCut={ops.cut}
        onPaste={ops.paste}
        onDuplicate={ops.duplicate}
        onSplit={ops.split}
        onLoop={loopSelection}
        onConsolidate={() => void consolidate()}
        consolidating={consolidating}
        onNudge={ops.nudge}
        onMoveLane={ops.moveLane}
        onToggleMute={ops.toggleMute}
        onDelete={ops.remove}
        compact
        dense={dense}
      />
    ) : null;
  const actionBar = makeActionBar(inlineActions);
  const editorPanel = renderEditorPanel(!narrow ? makeActionBar(true) : undefined);
  // The device view: the selected track's chain along the bottom, while the
  // clip editor isn't up there (Live shows one or the other).
  const deviceLaneTrack = selectedLaneId ? room?.tracks.find((t) => t.id === selectedLaneId) : undefined;
  const deviceLane =
    deviceLaneTrack && deviceViewOpen && !(editorSheet.open && editorPanel) && view === "timeline" ? (
      <DeviceLane
        title={cleanTrackName(deviceLaneTrack.name)}
        color={(() => {
          const lane = arrangement?.lanes.find((l) => l.id === deviceLaneTrack.id);
          return lane ? clipColor(lane.clips[0]?.color ?? 0, arrangement!.lanes.indexOf(lane)) : undefined;
        })()}
        kind={deviceLaneTrack.trackKind === "midi" ? "midi" : "audio"}
        // With no Set to report a chain, an unknown one is an empty one the browser can add to.
        devices={devicesByLane[deviceLaneTrack.id] ?? (liveInRoom ? undefined : [])}
        bpm={tempo}
        onParam={listening ? undefined : onDeviceParam}
        onPreview={onDevicePreview}
        onInsert={listening ? undefined : onDeviceInsert}
        onRemove={listening ? undefined : onDeviceRemove}
        busy={editor.busy}
        hear={hearDevices}
        onHear={setHearDevices}
        onClose={() => setDeviceViewOpen(false)}
        compact={narrow}
      />
    ) : null;
  const clipEditor = !narrow
    ? editorSheet.open || !editorPanel || !editingClip
      ? editorPanel
      : (
        <aside
          key={editingClip.id}
          className="flash-in flex h-9 shrink-0 select-none items-center gap-1 border-t border-panelline px-3 text-xs"
          style={{ background: `linear-gradient(90deg, ${editingColor}26 0%, var(--sunk, transparent) 320px)` }}
        >
          <button
            type="button"
            onClick={() => showEditor("half")}
            aria-expanded={false}
            aria-label="Open the clip editor"
            title={view === "pads" ? "Open the clip editor (or hold a pad)" : "Open the clip editor (or double-click a clip)"}
            className="flex min-w-0 flex-1 items-center gap-2 self-stretch text-left"
          >
            <ChevronDown className="size-4 shrink-0 rotate-180 text-muted-foreground" />
            <span className="size-3 shrink-0 rounded-sm" style={{ backgroundColor: editingColor }} />
            <span className="min-w-[3rem] truncate font-semibold">{editingClip.name || "Clip"}</span>
            <span className="min-w-0 shrink-[4] truncate text-muted-foreground">{editingMeta}</span>
          </button>
          {heard && (
            <button
              type="button"
              onClick={previewEditing}
              aria-label={previewing ? "Stop — everything comes back" : "Hear this clip alone, in time"}
              className={cn("flex size-7 shrink-0 items-center justify-center rounded-md hover:bg-foreground/10", previewing ? "text-primary" : "text-foreground/80")}
            >
              {previewing ? <Square className="size-3.5 fill-current" /> : <Play className="size-4 fill-current" />}
            </button>
          )}
          <button
            type="button"
            onClick={() => setEditingClipId(null)}
            aria-label="Close clip editor"
            className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-foreground/10"
          >
            <X className="size-4" />
          </button>
        </aside>
      )
    : editorPanel && editingClip
      ? (
        <aside
          className={cn(
            "flex flex-col border-t border-panelline bg-sunk transition-[height] duration-200",
            // Open, its height is what it asks for and no more than the
            // column has: at full on a short screen it gives way rather
            // than painting over the shelf's row and the transport.
            editorSheet.open ? "min-h-0 shrink" : "shrink-0",
            editorSheet.heightClass,
          )}
          style={{ background: `linear-gradient(180deg, ${editingColor}26 0%, var(--sunk, transparent) 56px)` }}
        >
          <div key={editingClip.id} className="flash-in relative flex min-h-11 shrink-0 touch-none select-none items-center gap-1 px-3 text-xs" {...editorSheet.handle}>
            <span aria-hidden className="absolute left-1/2 top-1.5 h-1 w-9 -translate-x-1/2 rounded-full bg-foreground/25" />
            <button
              type="button"
              onClick={() => (editorSheet.open ? hideEditor() : showEditor(openSnap))}
              aria-expanded={editorSheet.open}
              aria-label={editorSheet.open ? "Shrink the clip editor" : "Open the clip editor"}
              className={cn(
                "flex min-w-0 items-center gap-2 self-stretch py-2 text-left",
                inlineActions ? "max-w-[30%] shrink" : "flex-1",
              )}
            >
              <ChevronDown className={cn("size-5 shrink-0 transition-transform", !editorSheet.open && "-rotate-90")} />
              <span className="size-3 shrink-0 rounded-sm" style={{ backgroundColor: editingColor }} />
              <span className="min-w-[3rem] truncate font-semibold">{editingClip.name || "Clip"}</span>
              {!inlineActions && <span className="min-w-0 shrink-[4] truncate text-muted-foreground">{editingMeta}</span>}
            </button>
            {inlineActions && actionBar}
            {editorSheet.open && (
              <>
                <button type="button" onClick={editor.undo} disabled={!editor.canUndo} aria-label="Undo" className="flex size-10 shrink-0 items-center justify-center rounded-lg text-foreground/80 disabled:opacity-30">
                  <Undo2 className="size-5" />
                </button>
                <button type="button" onClick={editor.redo} disabled={!editor.canRedo} aria-label="Redo" className="flex size-10 shrink-0 items-center justify-center rounded-lg text-foreground/80 disabled:opacity-30">
                  <Redo2 className="size-5" />
                </button>
              </>
            )}
            {heard && (
              <button
                type="button"
                onClick={previewEditing}
                aria-label={previewing ? "Stop — everything comes back" : "Hear this clip alone, in time"}
                className={cn("flex size-10 shrink-0 items-center justify-center rounded-lg", previewing ? "text-primary" : "text-foreground/80")}
              >
                {previewing ? <Square className="size-4 fill-current" /> : <Play className="size-5 fill-current" />}
              </button>
            )}
            <button
              type="button"
              onClick={() => setEditingClipId(null)}
              aria-label="Close clip editor"
              className="flex size-10 shrink-0 items-center justify-center rounded-lg text-muted-foreground"
            >
              <X className="size-5" />
            </button>
          </div>
          {/* Cut, copy, duplicate, split, nudge, delete — Live keeps these in
              the clip's menu; the phone keeps them under the clip's name
              while the sheet is down. Open, the room is the editor's: the
              actions are one tap on the strip away. */}
          {actionBar && !inlineActions && !editorSheet.open && (
            <div className="shrink-0 border-t border-panelline/60 px-2 py-1">{actionBar}</div>
          )}
          {editorSheet.open && editorPanel}
        </aside>
      )
      : null;

  // What a row's ＋ would do right now, in words — the phone's substitute for
  // watching a drag: "Keys @ 9.1", "a new track", "pad 3 on Kick".
  const describeTarget = (payload: MaterialPayload): string | null => {
    const bar = (beats: number) => `${Math.floor(beats / 4) + 1}.${Math.floor(beats % 4) + 1}`;
    const at = Math.max(0, (player.insertSec * tempo) / 60);
    const laneName = (id: string) => cleanTrackName(displayNameOf(room.tracks.find((t) => t.id === id)?.name ?? "")) || "its track";
    if (view === "pads") {
      if ("material" in payload) {
        const source = room.clips.find((c) => c.id === payload.material);
        if (!source) return null;
        const picked = pickedSlot(source.clipKind === "midi" ? "midi" : "audio");
        const row = picked?.row ?? emptyRowOn(source.trackId);
        return row === null ? "nowhere — its track's pads are full" : `pad ${row + 1} on ${laneName(picked?.laneId ?? source.trackId)}`;
      }
      if ("mineMidi" in payload) {
        // Pads take audio from Mine for now; a MIDI idea lands on the timeline.
        const lane = room.tracks.find((t) => t.id === selectedLaneId && kindOf(t.id) === "midi");
        return lane ? `${laneName(lane.id)} @ ${bar(at)}` : `a new MIDI track @ ${bar(at)}`;
      }
      const picked = pickedSlot("audio");
      if (picked) return `pad ${picked.row + 1} on ${laneName(picked.laneId)}`;
      const lane = room.tracks.find((t) => t.id === selectedLaneId && kindOf(t.id) === "audio");
      const row = lane ? emptyRowOn(lane.id) : null;
      return lane && row !== null ? `pad ${row + 1} on ${laneName(lane.id)}` : "a new track";
    }
    if ("material" in payload) {
      const source = room.clips.find((c) => c.id === payload.material);
      return source ? `${laneName(source.trackId)} @ ${bar(at)}` : null;
    }
    const selected = selectedLaneId ? room.tracks.find((t) => t.id === selectedLaneId) : null;
    if ("mineMidi" in payload) {
      // A MIDI idea: the selected MIDI lane, else a lane of its own.
      return selected && kindOf(selected.id) === "midi" ? `${laneName(selected.id)} @ ${bar(at)}` : `a new MIDI track @ ${bar(at)}`;
    }
    if ("reference" in payload) {
      const lane = selected && kindOf(selected.id) === "audio" ? selected : null;
      return `Mine, then ${lane ? laneName(lane.id) : "a new track"} @ ${bar(at)}`;
    }
    const clip = mine.clips.find((c) => c.id === payload.mine);
    const named = clip ? room.tracks.find((t) => kindOf(t.id) === "audio" && displayNameOf(t.name) === clip.name) : null;
    const lane = selected && kindOf(selected.id) === "audio" ? selected : named;
    return lane ? `${laneName(lane.id)} @ ${bar(at)}` : `a new track @ ${bar(at)}`;
  };

  // The material rail, beside the timeline and beside the pads.
  const materialShelf = !listening && (library.distinctClips > 0 || mine.shelf.signedIn || newSeed !== undefined) ? (
              <aside
                className={cn(
                  "flex shrink-0 flex-col border-panelline bg-sunk transition-[height] duration-200",
                  "border-t lg:border-l lg:border-t-0",
                  narrow && materialSheet.heightClass,
                  // On its side, a clip in the editor sheet takes the shelf's
                  // peek row: one sheet at a time already, and 44px is a lane.
                  // Hidden, not unmounted — a carried row lives in the shelf.
                  // Upright, the same once the editor is open: its 44px is
                  // the editor's tab panel, and pulling the editor down to
                  // its strip brings the row back.
                  narrow && (short ? clipEditor !== null : editorSheet.open) && !shelfOpen && !shelfLifting && "hidden",
                  !narrow && (libraryOpen ? "lg:w-[340px] xl:w-[380px]" : "lg:w-11"),
                )}
              >
                <div
                  className={cn(
                    // A phone reaches this by thumb, so the row is a thumb's
                    // height — it used to be 11px of text with 3px of padding
                    // around it, which is a target you aim at rather than hit.
                    // No vertical padding of its own on a phone: the button below stretches
                    // to the full row, so the whole 44px is the target rather than 36
                    // with a dead margin around it.
                    "relative flex min-h-11 shrink-0 select-none items-center gap-2 px-3 text-xs text-muted-foreground lg:min-h-0 lg:py-1.5 lg:text-[11px]",
                    // On a phone the header is the sheet's handle.
                    narrow && "touch-none",
                    // Collapsed on a wide screen the header turns on its side
                    // and becomes the whole rail: a strip you can still find
                    // and reopen, rather than a control that vanished.
                    !shelfOpen && "lg:h-full lg:flex-col lg:justify-start lg:px-0 lg:py-3",
                  )}
                  {...shelfHandle}
                >
                  {narrow && <span aria-hidden className="absolute left-1/2 top-1.5 h-1 w-9 -translate-x-1/2 rounded-full bg-foreground/25" />}
                  <button
                    type="button"
                    onClick={toggleShelf}
                    aria-expanded={shelfOpen}
                    aria-label={shelfOpen ? "Hide the material shelf" : "Show the material shelf"}
                    className={cn(
                      "flex min-w-0 flex-1 items-center gap-2 self-stretch py-2 text-left hover:text-foreground lg:flex-none lg:self-auto lg:py-0",
                      !shelfOpen && "lg:flex-col lg:gap-3",
                    )}
                  >
                    <ChevronDown
                      className={cn(
                        "size-5 shrink-0 transition-transform lg:size-3.5",
                        !shelfOpen && "-rotate-90",
                        !shelfOpen && "lg:rotate-90",
                      )}
                    />
                    <span
                      className={cn(
                        "font-semibold uppercase tracking-widest",
                        !shelfOpen && "lg:[writing-mode:vertical-rl]",
                      )}
                    >
                      Material
                    </span>
                    <span className={cn("truncate", !shelfOpen && "lg:hidden")}>
                      {(() => {
                        if (mine.filter === "recent") return "Recent";
                        // The place and what it holds, the same shape for each.
                        if (mine.filter === "mine" && !mine.shelf.signedIn) return "Mine";
                        const n =
                          mine.filter === "mine"
                            ? mine.shelf.clips.length + mine.shelf.midi.length
                            : mine.filter === "seeds"
                              ? mine.shelf.seeds.reduce((sum, s) => sum + s.clips.length + (s.midi?.length ?? 0), 0)
                              : library.distinctClips;
                        const place = mine.filter === "mine" ? "Mine" : mine.filter === "seeds" ? "Seeds" : "Project";
                        return `${place} · ${n} clip${n === 1 ? "" : "s"}`;
                      })()}
                    </span>
                  </button>
                  {launchedCount > 0 && (
                    <span
                      className={cn(
                        "ml-auto flex items-center gap-1 text-primary",
                        !shelfOpen && "lg:ml-0 lg:hidden",
                      )}
                    >
                      <span className="size-1.5 animate-pulse rounded-full bg-primary" />
                      {launchedCount} on Pads
                    </span>
                  )}
                </div>
                {(shelfOpen || shelfLifting) && (
                  <div className={cn("flex min-h-0 min-w-0 flex-1 border-t border-panelline/60 pt-2", !shelfOpen && "h-0 flex-none overflow-hidden border-0 pt-0")}>
                    <MaterialLibrary
                      library={library}
                      player={player}
                      onPlace={view === "pads" ? placeMaterialInPads : placeMaterial}
                      filter={mine.filter}
                      onFilter={mine.setFilter}
                      mine={{
                        ...mine.shelf,
                        onDownload: downloads.downloadMine,
                        swapTarget: editing ? { clipId: editing.clip.id, name: editing.clip.name } : null,
                        onSwap: editing ? (clip) => mine.swapAudio(editing.clip.id, clip) : undefined,
                        ...(view === "pads" ? { onPlace: placeMineInPads, placeTarget: "pads" as const } : {}),
                      }}
                      target={view === "pads" ? "pads" : "timeline"}
                      onDownload={(clipId) => downloads.askClip(clipId, "materials")}
                      canDownload={downloads.canDownloadClip}
                      tempo={tempo}
                      roomId={jamId ?? seedId ?? "local"}
                      describeTarget={describeTarget}
                      onLifting={onShelfLifting}
                    />
                  </div>
                )}
              </aside>
            ) : null;

  const playable = sources.length + midi.length > 0;
  const playing = player.status === "playing";
  const position = formatTime(player.position);
  const duration = formatTime(player.duration);

  return (
    <div className="relative flex h-dvh flex-col overflow-hidden bg-room text-foreground">
      {stage && <StageHandleBridge stage={stage} playing={playing} stop={playback.transport.stop} ask={guard.ask} />}
      {fileDragOver && !listening && (
        <div className="pointer-events-none fixed inset-2 z-[60] flex items-end justify-center rounded-2xl border-2 border-dashed border-primary/60 bg-[color-mix(in_srgb,var(--primary)_6%,transparent)] pb-10">
          <span className="rounded-full bg-raised px-4 py-2 text-sm font-semibold shadow-lg">
            Drop on a track to add it there, anywhere else for a new track
          </span>
        </div>
      )}
      {/* Ambient background glows. Absolute, inside this clipped full-screen
          box — `fixed` escaped the clip, and a 600px circle hanging off a
          390px phone widened the page, so iOS opened the room zoomed in and
          scrollable sideways. Gradients rather than blur (AmbientGlow's
          reason: iOS's graphics budget). */}
      {/* Their own clipped box, too: hanging past the room's edge they widened
          its scroll area, and focusing a field near that edge (the phone's
          tempo) scrolled the whole room sideways. */}
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div
          className="absolute -left-[10%] -top-[20%] h-[600px] w-[600px]"
          style={{ background: "radial-gradient(closest-side, color-mix(in srgb, var(--primary) 8%, transparent), transparent)" }}
        />
        <div
          className="absolute -bottom-[20%] -right-[10%] h-[500px] w-[500px]"
          style={{ background: "radial-gradient(closest-side, rgba(28, 57, 142, 0.15), transparent)" }}
        />
      </div>

      {/* ---- Mobile header ---- */}
      {/* Two rows upright; one on a phone held sideways (short:), where the
          height is the scarce thing and the width is not. */}
      <header className="sticky top-0 z-30 bg-room border-b border-panelline wide:hidden short:flex short:items-center">
        <div className="h-14 px-3 flex items-center gap-2 short:min-w-0 short:flex-1">
          {stage ? stage.entry : (
            <Link
              to={backTo}
              aria-label="Back"
              onClick={(e) => guard.confirmLeave(e, backTo)}
              className="flex size-10 shrink-0 items-center justify-center text-foreground/80"
            >
              <ArrowLeft className="size-5" />
            </Link>
          )}
          <div className="flex-1 min-w-0">
            {newSeed ? (
              <TitleField value={title} onChange={newSeed.onTitle} className="text-sm" />
            ) : (
              <p className="font-semibold text-sm truncate">{title}</p>
            )}
            <div className="text-[11px] text-muted-foreground truncate">
              {presence
                ? <PresenceBar participants={presence.participants} status={presence.status} isHost={presence.isHost} listening={presence.listening} />
                : editor.dirty
                  ? <span className="text-primary">{editor.editDepth} change{editor.editDepth !== 1 ? "s" : ""}, unsaved</span>
                  : "no changes yet"}
            </div>
          </div>
          {listen ? (
            <ListenActions listen={listen} compact />
          ) : (
            <>
              <button
                type="button"
                onClick={() => setAddTrackOpen(true)}
                aria-label="New track"
                className="flex size-10 shrink-0 items-center justify-center rounded-lg text-foreground/80"
              >
                <Plus className="size-5" />
              </button>
              {seedId === null ? (
                newSeed && saveNewSeed(newSeed)
              ) : (
                <>
                  {!jamId && <StartJamButton seedId={seedId} size="bar" label="Jam" onBeforeStart={guard.ask} />}
                  <PublishRoom
                    seedId={seedId}
                    seedTitle={title}
                    dirty={editor.dirty}
                    room={room}
                    jamId={jamId}
                    localTakes={jamId ? undefined : takes.held}
                    renderMaster={renderMaster}
                    onPublished={editor.markClean}
                  />
                </>
              )}
            </>
          )}
        </div>
        {/* Undo and redo ride with the views rather than the transport: the
            transport ran past a 390px screen's edge once record joined it, and
            here they reach every view, Pads included. */}
        <div className="px-3 pb-3 flex items-center gap-1 short:pb-0 short:pl-0 short:w-[22rem] short:shrink-0">
          <div className="grid flex-1 grid-cols-3 gap-1">
            {/* A phone's order: its default, Pads, first. */}
            {(["pads", "timeline", "mixer"] as const).map((v) => (
              <ViewSwitch key={v} view={v} active={view === v} onClick={() => setView(v)} />
            ))}
          </div>
          {!listening && (
            <>
              <button
                type="button"
                disabled={!editor.canUndo}
                onClick={editor.undo}
                className="size-8 rounded-lg text-foreground/70 flex items-center justify-center shrink-0 disabled:opacity-30"
                aria-label={editor.undoLabel ? `Undo: ${editor.undoLabel}` : "Undo"}
              >
                <Undo2 className="size-4" />
              </button>
              <button
                type="button"
                disabled={!editor.canRedo}
                onClick={editor.redo}
                className="size-8 rounded-lg text-foreground/70 flex items-center justify-center shrink-0 disabled:opacity-30"
                aria-label={editor.redoLabel ? `Redo: ${editor.redoLabel}` : "Redo"}
              >
                <Redo2 className="size-4" />
              </button>
            </>
          )}
        </div>
      </header>

      {/* ---- Desktop header ---- */}
      <header className="z-10 hidden wide:flex shrink-0 flex-col gap-2 border-b border-panelline bg-sunk px-3 py-2 lg:h-16 lg:flex-row lg:items-center lg:gap-4 lg:px-4 lg:py-0">
        <div className="flex min-w-0 items-center gap-3 lg:min-w-[6rem] lg:flex-1">
          {stage ? stage.entry : (
            <Link
              to={backTo}
              aria-label="Back"
              onClick={(e) => guard.confirmLeave(e, backTo)}
              className="flex size-9 shrink-0 items-center justify-center rounded-lg text-foreground/70 hover:bg-white/5"
            >
              <ArrowLeft className="size-5" />
            </Link>
          )}
          <div className="min-w-0">
            {newSeed ? (
              <TitleField value={title} onChange={newSeed.onTitle} className="text-sm" />
            ) : (
              <p className="truncate text-sm font-semibold">{title}</p>
            )}
            <div className="truncate text-[11px] text-muted-foreground">
              {presence
                ? <PresenceBar participants={presence.participants} status={presence.status} isHost={presence.isHost} listening={presence.listening} />
                : editor.dirty
                  ? <span className="text-primary">{editor.editDepth} change{editor.editDepth !== 1 ? "s" : ""}, unsaved</span>
                  : "no changes yet"}
            </div>
          </div>
        </div>

        {/* Center: transport. Scrolls rather than squashes on a narrow screen —
            the clock and the tempo are readings, and a squashed reading lies. */}
        <div className="flex shrink-0 items-center gap-2 overflow-x-auto rounded-xl border border-panelline bg-panel p-1.5 scrollbar-thin">
          <button
            type="button"
            disabled={!playable}
            onClick={playback.transport.play}
            className={cn(
              "flex size-10 shrink-0 items-center justify-center rounded-lg",
              playing ? "bg-primary text-primary-foreground" : "bg-primary text-primary-foreground hover:bg-white",
            )}
            aria-label={playing ? "Stop" : "Play"}
            title={playing ? "Stop — back to the insert marker (Space)" : "Play from the insert marker (Space)"}
          >
            {playing ? <Square className="size-4 fill-current" /> : <Play className="size-5" />}
          </button>
          <button type="button" onClick={stopAll} onDoubleClick={playback.transport.panic} className="flex size-10 items-center justify-center rounded-lg text-foreground/70 hover:bg-white/5" aria-label="Stop" title="Stop the arrangement and previews; pads play on. Twice: everything, back to the start">
            <Square className="size-4" />
          </button>
          <button
            type="button"
            onClick={() => player.setLoop(!player.loop)}
            className={cn("flex size-10 items-center justify-center rounded-lg", player.loop ? "bg-white/[0.06] text-primary" : "text-muted-foreground hover:bg-white/5")}
            aria-label={player.loop ? "Disable loop" : "Enable loop"}
          >
            <Repeat className="size-4" />
          </button>
          {(view === "timeline" || rec.recording) && !listening && (
            <>
              <RecordButton rec={rec} className="size-10" />
              <RecordSettings rec={rec} className="h-10 w-5 -ml-1.5" />
            </>
          )}
          <div className="mx-1 h-8 w-px bg-panelline" />
          <div className="px-2 text-center">
            <div className="font-mono text-lg leading-none tracking-tight">{position}</div>
            <div className="mt-1 text-[9px] uppercase tracking-widest text-muted-foreground">time</div>
          </div>
          <div className="px-2 text-center">
            <div className="font-mono text-lg leading-none text-foreground/70">{duration}</div>
            <div className="mt-1 text-[9px] uppercase tracking-widest text-muted-foreground">length</div>
          </div>
          <div className="mx-1 h-8 w-px bg-panelline" />
          <div className="px-2 text-center">
            <TempoReading
              tempo={tempo}
              onCommit={listening ? undefined : (bpm) => editor.propose([tempoIntent(room, bpm)])}
            />
            <div className="mt-1 text-[9px] uppercase tracking-widest text-muted-foreground">bpm</div>
          </div>
        </div>

        {/* Right: view switcher + actions */}
        <div className="flex flex-wrap items-center justify-end gap-2 lg:flex-1 lg:flex-nowrap">
          {/* Icons until 2xl: at 1280 the named tabs pushed Save version off
              the right edge and squeezed the title to "Nigh…". */}
          <div className="flex items-center rounded-xl border border-panelline bg-panel p-1">
            {/* Arrange first, the view this width opens on (Ableton's
                Arrangement beside Session, FL's Playlist beside its rack). */}
            {(["timeline", "pads", "mixer"] as const).map((v) => (
              <ViewSwitch key={v} view={v} iconOnly active={view === v} onClick={() => setView(v)} />
            ))}
          </div>
          {!listening && (
            <>
              {/* Arrange has its own, heading the track list. */}
              {view !== "timeline" && (
                <Button variant="outline" size="sm" onClick={() => setAddTrackOpen(true)} title="New track — an instrument or audio" aria-label="New track">
                  <Plus className="size-4" />
                  <span className="hidden 2xl:inline">Track</span>
                </Button>
              )}
              <Button variant="ghost" size="icon" disabled={!editor.canUndo} onClick={editor.undo} title={editor.undoLabel ? `Undo: ${editor.undoLabel}` : "Undo"}>
                <Undo2 className="size-4" />
              </Button>
              <Button variant="ghost" size="icon" disabled={!editor.canRedo} onClick={editor.redo} title={editor.redoLabel ? `Redo: ${editor.redoLabel}` : "Redo"}>
                <Redo2 className="size-4" />
              </Button>
            </>
          )}
          {!narrow && (
            <Button
              variant="ghost"
              size="icon"
              className="hidden 2xl:inline-flex"
              onClick={() => setShortcutsOpen((open) => !open)}
              aria-label="Keyboard shortcuts (?)"
              title="Keyboard shortcuts — ?"
            >
              <Keyboard className="size-4" />
            </Button>
          )}
          {listen ? (
            <ListenActions listen={listen} />
          ) : (
            <>
              {/* The room as it is now — every track's audio and the mix. */}
              <DownloadButton
                label={`Download “${title}” (.zip) — the audio on every track, and the mix`}
                why="Sign in to download this seed"
                className="size-9 hover:bg-white/5"
                run={downloads.downloadSeed}
              />
              {/* A browser remix is solo until it's a jam: open one on this
                  seed and hand out the room link. */}
              {seedId === null ? (
                newSeed && saveNewSeed(newSeed)
              ) : (
                <>
                  {!jamId && <StartJamButton seedId={seedId} size="bar" label="Jam" onBeforeStart={guard.ask} />}
                  <PublishRoom
                    seedId={seedId}
                    seedTitle={title}
                    dirty={editor.dirty}
                    room={room}
                    jamId={jamId}
                    localTakes={jamId ? undefined : takes.held}
                    renderMaster={renderMaster}
                    onPublished={editor.markClean}
                  />
                </>
              )}
            </>
          )}
        </div>
      </header>

      {listen && <ListenBanner listen={listen} />}

      {addTrackSheet}

      {/* ---- Main views ---- */}
      <main className="flex-1 min-h-0 relative">
        {fileDrop.note && (
          <div
            role="status"
            aria-live="polite"
            className={cn(
              "absolute left-1/2 z-20 flex max-w-[90%] -translate-x-1/2 items-center gap-3 rounded-lg border border-panelline bg-raised px-3 py-2 text-xs shadow-lg",
              rec.note ? "bottom-14" : "bottom-3",
              fileDrop.note.error ? "text-[var(--changed)]" : "text-foreground",
            )}
          >
            <span>{fileDrop.note.text}</span>
            <button type="button" onClick={fileDrop.dismissNote} aria-label="Dismiss" className="text-muted-foreground hover:text-foreground">
              ×
            </button>
          </div>
        )}
        {rec.note && (
          <div
            role="status"
            aria-live="polite"
            className="absolute bottom-3 left-1/2 z-20 flex max-w-[90%] -translate-x-1/2 items-center gap-3 rounded-lg border border-panelline bg-raised px-3 py-2 text-xs text-[var(--changed)] shadow-lg"
          >
            <span>{rec.note}</span>
            <button type="button" onClick={rec.dismissNote} aria-label="Dismiss" className="text-muted-foreground hover:text-foreground">
              ×
            </button>
          </div>
        )}
        {view === "mixer" ? (
          <>
            {/* The mixer's channel strips are vertical faders side by side —
                fine for a pointer on a wide screen, unusable at 390px. On a
                phone the same controls lie down: one row per lane, the fader
                horizontal, the readout at the end of it. */}
            <div className="absolute inset-0 overflow-auto p-3 wide:hidden">
              {/* One line a lane: at a card each, five tracks and the master
                  did not fit one phone screen. The name opens the lane's pan. */}
              <div className="flex flex-col divide-y divide-panelline overflow-hidden rounded-2xl border border-panelline bg-raised">
                {arrangement.lanes.map((lane, i) => {
                  const isMuted = audio.muted.has(lane.id);
                  const isSoloed = audio.soloed.has(lane.id);
                  const laneAudible = isAudible(isMuted, isSoloed, audio.soloed.size > 0);
                  const color = clipColor(lane.clips[0]?.color ?? 0, i);
                  const name = cleanTrackName(lane.name);
                  // The same fader position the desktop strip uses — 0..1,
                  // unity at 0.85, read through shared/mixer.ts.
                  const vol = levels[lane.id]?.volume ?? lane.volume;
                  const pan = levels[lane.id]?.pan ?? lane.pan;
                  const panOpen = mixPanLane === lane.id;
                  const set = (next: { volume: number; pan: number }) => {
                    applyLevel(lane.id, next);
                    player.setLaneLevel(lane.id, next);
                  };
                  return (
                    <div key={lane.id} className="px-3 py-1.5">
                      <div className={cn("flex items-center gap-2", !laneAudible && "opacity-50")}>
                        <button
                          type="button"
                          onClick={() => setMixPanLane(panOpen ? null : lane.id)}
                          aria-expanded={panOpen}
                          aria-label={`${name} — ${panOpen ? "hide" : "show"} pan`}
                          className="flex w-24 shrink-0 items-center gap-2 py-1.5 text-left"
                        >
                          <span className="h-7 w-1 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-semibold">{name}</span>
                            <span className="block text-[10px] text-muted-foreground">{panLabel(pan)}</span>
                          </span>
                        </button>
                        <PhoneSlider value={vol} onChange={(v) => set({ volume: v, pan })} label={`${name} volume`} />
                        <span className="w-12 shrink-0 text-right font-mono text-[10px] text-muted-foreground whitespace-nowrap">{faderDbLabel(vol)}</span>
                        <button
                          type="button"
                          onClick={() => audio.onMute(lane.id, !isMuted)}
                          aria-pressed={isMuted}
                          aria-label={`Mute ${name}`}
                          className={cn("w-8 h-8 shrink-0 rounded-lg text-[11px] font-bold flex items-center justify-center", isMuted ? "bg-mute text-mute-ink" : "bg-white/5 text-muted-foreground")}
                        >
                          M
                        </button>
                        <button
                          type="button"
                          onClick={() => audio.onSolo(lane.id, !isSoloed)}
                          aria-pressed={isSoloed}
                          aria-label={`Solo ${name}`}
                          className={cn("w-8 h-8 shrink-0 rounded-lg text-[11px] font-bold flex items-center justify-center", isSoloed ? "bg-solo text-solo-ink" : "bg-white/5 text-muted-foreground")}
                        >
                          S
                        </button>
                      </div>
                      {panOpen && (
                        <div className="flex items-center gap-2 pb-1 pl-3">
                          <span className="w-[5.25rem] shrink-0 text-[11px] text-muted-foreground">Pan</span>
                          <PhoneSlider value={pan} min={-1} onChange={(p) => set({ volume: vol, pan: p })} label={`${name} pan`} centred />
                          <button
                            type="button"
                            onClick={() => set({ volume: vol, pan: 0 })}
                            className="w-[5.5rem] shrink-0 text-right font-mono text-[10px] text-muted-foreground"
                            aria-label={`Centre ${name}`}
                          >
                            {panLabel(pan)} · centre
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="flex items-center gap-2 bg-white/[0.03] px-3 py-1.5">
                  <span className="flex w-24 shrink-0 items-center gap-2 py-1.5">
                    <span className="h-7 w-1 shrink-0 rounded-full bg-foreground/80" />
                    <span className="text-sm font-semibold">Master</span>
                  </span>
                  <PhoneSlider value={masterVolume} onChange={(v) => { setMasterVolume(v); player.setVolume(v); }} label="Master volume" />
                  <span className="w-12 shrink-0 text-right font-mono text-[10px] text-muted-foreground whitespace-nowrap">{gainDbLabel(masterVolume)}</span>
                  <span className="w-[4.5rem] shrink-0" />
                </div>
              </div>
            </div>
            <div className="absolute inset-0 overflow-auto p-4 hidden wide:block wide:p-6">
            <MixerView
              lanes={arrangement.lanes}
              levels={levels}
              onLevel={applyLevel}
              onReset={(laneId) => {
                const lane = arrangement.lanes.find((l) => l.id === laneId);
                if (lane) applyLevel(laneId, { volume: lane.volume, pan: lane.pan });
              }}
              audio={audio}
              launch={{ playing: player.sessionPlaying, overridden: player.overridden, onStopLane: launch.stopLane, onBackToArrangement: launch.backToArrangement }}
              meters={{ level: player.meterLevel, master: player.masterLevel, active: playing || Object.keys(player.sessionPlaying).length > 0 }}
              master={{ volume: masterVolume, onVolume: (v) => { setMasterVolume(v); player.setVolume(v); } }}
            />
            </div>
          </>
        ) : view === "pads" ? (
          // The grid scrolls itself (headers and scene column frozen), and the
          // clip editor docks under it as it does under the timeline — a
          // sheet over it on a phone.
          // The shelf rides beside the pads too (under them on a phone), so a
          // loop goes straight into a pad.
          <div className="absolute inset-0 flex min-h-0 flex-col lg:flex-row">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            {!listening && arrangement.lanes.length === 0 ? (
              emptyRoomPrompt
            ) : scenes && scenes.rows.length > 0 ? (
              <div className="mx-auto flex min-h-0 w-full max-w-[1280px] flex-1 flex-col">
                <SessionPads
                  grid={scenes}
                  launch={padLaunch}
                  mixer={padMixer}
                  quantize={padQuantize}
                  onQuantize={setPadQuantize}
                  waveform={padWaveform}
                  edit={listening ? undefined : padEdit}
                />
              </div>
            ) : (
              <div className="p-4 wide:p-6">
                <div className="mx-auto max-w-[520px] rounded-2xl border border-dashed border-panelline p-10 text-center text-sm text-muted-foreground">
                  Nothing in this Set&rsquo;s Session View, and nothing loop-shaped on its timeline to offer as pads.
                  The Arrange tab has its clips.
                </div>
              </div>
            )}
            {clipEditor}
          </div>
          {materialShelf}
          </div>
        ) : (
          // Desktop: the timeline with the material rail down its right-hand
          // side, where a palette belongs next to the thing it feeds. Phone:
          // the same rail becomes a sheet under the timeline, because 320px of
          // side rail on a 390px screen is not a side rail. One instance of
          // the shelf either way — the breakpoint only turns the axis.
          <div className="absolute inset-0 flex min-h-0 flex-col lg:flex-row">
            <div className="flex min-h-[9rem] min-w-0 flex-1 flex-col lg:min-h-0">
              {/* The view lays its lanes out at their natural height, so a Set
                  with more tracks than fit would paint straight over whatever
                  sits below it. Scroll here, not bleed. */}
              <div ref={setTimelineBox} className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
                <ArrangementView
                  arrangement={arrangement}
                  live
                  emptyState={listening ? undefined : emptyRoomPrompt}
                  // A double-click, and a finger's hold, open the editor.
                  onMidiOpen={listening ? undefined : (clip) => openEditor(clip.id)}
                  onAudioOpen={listening ? undefined : (clip) => openEditor(clip.id)}
                  onAudioHold={listening ? undefined : (clip) => openEditor(clip.id)}
                  playheadBeats={playheadBeats}
                  // A click on the timeline places the playhead — and with
                  // it where the transport starts and where the shelf's ＋
                  // drops material. On the beat, not on the pixel: a thumb on
                  // a phone cannot aim at 1/480th of a bar, and material that
                  // lands at beat 37.4213 is off the grid for good.
                  // Live's two places to click. A track moves the insert marker
                  // (where Space plays from) and leaves the music playing; the
                  // ruler plays from there, or jumps on the next bar if playing.
                  // On the beat, not on the pixel: a thumb cannot aim at
                  // 1/480th of a bar.
                  // Editing, the view has already put it on the grid it draws.
                  onSeekBeats={(b) => player.setInsert(beatsToSeconds(Math.max(0, edit ? b : snap(b, GRID)), tempo))}
                  onScrubBeats={(b) => player.jumpTo(beatsToSeconds(Math.max(0, snap(b, grid > 0 ? grid : GRID)), tempo))}
                  insertBeats={(player.insertSec * tempo) / 60}
                  onLaneSelect={(id) => setSelectedLaneId(id === selectedLaneId ? null : id)}
                  onLaneFocus={(id) => {
                    setSelectedLaneId(id);
                    setDeviceViewOpen(true);
                  }}
                  selectedLaneId={selectedLaneId}
                  audio={audio}
                  edit={edit}
                  waveform={laneWaveform}
                  timeFormat="bars"
                  laneHeight={short ? "normal" : "tall"}
                  // A phone scrolls its lanes; a desktop with room to spare
                  // gives it to them instead of to empty floor.
                  fillHeight={narrow ? undefined : timelineRoom}
                  onNewTrack={listening ? undefined : () => setAddTrackOpen(true)}
                  contextMenu={listening ? undefined : arrangementContextMenu}
                />
                {!listening && arrangement.lanes.length > 0 && (
                  <div data-new-track-row className="px-3 py-3 wide:px-4">
                    <NewTrackRow
                      onClick={() => setAddTrackOpen(true)}
                      onDropMaterial={dropMaterialOnNewLane}
                      onDropMine={(id) => mine.onDropMineNewLane(insertNowRef.current, id)}
                      onDropMineMidi={(id) => onDropMineMidiNewLane(insertNowRef.current, id)}
                      onDropFiles={(dt) => fileDrop.dropAnywhere(dt, insertNowRef.current)}
                    />
                  </div>
                )}
              </div>

              {clipEditor}

            {/* The selection, and everything you can do to it.

                Tapping a clip selects it and opens it in the editor (the sheet
                under this row on a phone); this row is what Live keeps in a
                right-click menu — copy, paste, split, nudge, delete. A
                "Stop preview" joins it while the shelf auditions something. */}
            <div className={cn(
              "flex shrink-0 flex-wrap items-center gap-x-6 gap-y-2 border-t border-panelline bg-sunk px-4 py-3 wide:px-5 lg:h-[92px] lg:flex-nowrap lg:py-0",
              // Nothing selected, nothing on the clipboard: no row at all. It was
              // 92px of "click a clip" on the desktop, which the timeline's own
              // footer already says, under lanes that wanted the height.
              ops.selection.length === 0 && !ops.span && ops.clipboardSize === 0 && !player.auditioningId && !ops.note && !editNote && "hidden",
              // And while the editor sheet is up: the sheet carries the clip's
              // name and the same actions under it, so a second row would
              // only take the timeline's room.
              narrow && clipEditor !== null && "hidden",
              // Docked and open, the editor's strip carries the actions.
              !narrow && editorSheet.open && editorPanel !== null && !player.auditioningId && "hidden",
              listening && "hidden wide:hidden",
            )}>
              <div className="min-w-0 wide:min-w-[180px]">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground hidden wide:block">Selected clip</div>
                <div className="mt-0 wide:mt-1 flex items-center gap-2 font-semibold text-sm wide:text-base">
                  {player.auditioningId && <span className="size-2.5 shrink-0 animate-pulse rounded-sm bg-primary" />}
                  {ops.span
                    ? `${+(ops.span.end - ops.span.start).toFixed(3)} beat${ops.span.end - ops.span.start === 1 ? "" : "s"} · ${ops.span.laneIds.length} lane${ops.span.laneIds.length > 1 ? "s" : ""}`
                    : ops.selection.length > 0
                    ? `${ops.selection.length} clip${ops.selection.length > 1 ? "s" : ""}`
                    : ops.clipboardSize > 0
                      ? `${ops.clipboardSize} on clipboard`
                      : narrow ? "Tap a clip to open it" : "Double-click a clip to open it"}
                </div>
              </div>

              {/* Paste is live with nothing selected — copy a clip, move the
                  playhead, paste is the whole gesture on a phone — so the row
                  outlives the selection that opened it. */}
              {(ops.selection.length > 0 || ops.span || ops.clipboardSize > 0) && (
                <ClipActionBar
                  can={ops.can}
                  muted={ops.muted}
                  onCopy={ops.copy}
                  onCut={ops.cut}
                  onPaste={ops.paste}
                  onDuplicate={ops.duplicate}
                  onSplit={ops.split}
                  onLoop={loopSelection}
                  onConsolidate={listening ? undefined : () => void consolidate()}
                  consolidating={consolidating}
                  onNudge={ops.nudge}
                  onMoveLane={ops.moveLane}
                  onToggleMute={ops.toggleMute}
                  onDelete={ops.remove}
                  compact={narrow}
                />
              )}

              {player.auditioningId && (
                <button
                  type="button"
                  onClick={preview.stop}
                  className="h-11 shrink-0 rounded-lg border border-panelline px-4 text-xs font-semibold text-danger hover:bg-danger/10 lg:h-8"
                >
                  Stop preview
                </button>
              )}

              <div className="ml-auto min-w-0 text-xs">
                {ops.note || editNote ? (
                  <span role="status" aria-live="polite" className="text-[var(--changed)]">
                    {ops.note ?? editNote}
                  </span>
                ) : (
                  <span className="hidden text-muted-foreground xl:inline">
                    Drag to move · drag edges to trim · ⇧-click to add to the selection
                  </span>
                )}
              </div>
              </div>

              {deviceLane}
            </div>

            {/* The lane a thumb just tapped, as a strip. The mixer tab has all
                of them; this is the one you are looking at, without leaving
                the timeline to reach it. */}
            {selectedLaneId && (() => {
              const lane = arrangement.lanes.find((l) => l.id === selectedLaneId);
              if (!lane) return null;
              const laneIdx = arrangement.lanes.indexOf(lane);
              const color = clipColor(lane.clips[0]?.color ?? 0, laneIdx);
              const vol = ((levels[selectedLaneId]?.volume ?? lane.volume) / 0.85) * 100;
              const dB = ((vol - 80) / 4).toFixed(1);
              const panVal = levels[selectedLaneId]?.pan ?? lane.pan;
              const panPct = ((panVal + 1) / 2) * 100;
              const panLbl = Math.abs(panVal) < 0.01 ? "C" : panVal < 0 ? `${Math.round(-panVal * 50)}L` : `${Math.round(panVal * 50)}R`;
              return (
                <div className="wide:hidden shrink-0 border-t border-panelline bg-sunk px-4 py-3">
                  {/* However long the track's name, it gives way to the close
                      button: the strip must always be one tap from gone. */}
                  <div className={cn("flex items-center justify-between gap-2", !deviceViewOpen && "mb-3")}>
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                      <span className="truncate text-sm font-semibold">{cleanTrackName(lane.name)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setDeviceViewOpen(!deviceViewOpen)}
                      aria-pressed={deviceViewOpen}
                      className={cn("h-8 shrink-0 rounded-lg px-3 text-xs font-semibold", deviceViewOpen ? "bg-white/10 text-primary" : "bg-white/5 text-foreground/70")}
                    >
                      Devices
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedLaneId(null)}
                      aria-label="Close track strip"
                      className="-my-2 -mr-2 flex size-10 shrink-0 items-center justify-center rounded-full text-muted-foreground active:bg-white/10 active:text-foreground"
                    >
                      <X className="size-5" />
                    </button>
                  </div>
                  {/* With the devices open the lane needs the height; the faders are a tap on Devices away. */}
                  <div className={cn("space-y-3", deviceViewOpen && "hidden")}>
                    <label className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="w-10">Vol</span>
                      <div className="flex-1 relative h-8 flex items-center">
                        <div className="absolute inset-x-0 h-1.5 rounded-full bg-strong" />
                        <div className="absolute left-0 h-1.5 rounded-full" style={{ width: `${Math.max(0, vol - 12)}%`, background: "linear-gradient(90deg, var(--primary) 70%, #e9d86a)" }} />
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={Math.round(vol)}
                          onChange={(e) => {
                            const v = (Number(e.target.value) / 100) * 0.85;
                            applyLevel(selectedLaneId, { volume: v, pan: panVal });
                            player.setLaneLevel(selectedLaneId, { volume: v, pan: panVal });
                          }}
                          className="absolute inset-0 w-full h-8 opacity-0 cursor-pointer"
                          aria-label={`${cleanTrackName(lane.name)} volume`}
                        />
                        <div className="absolute w-6 h-6 rounded-full bg-gradient-to-b from-white to-[#b9b9c2] shadow pointer-events-none" style={{ left: `calc(${vol}% - 12px)` }} />
                      </div>
                      <span className="font-mono text-[11px] w-14 text-right">{dB} dB</span>
                    </label>
                    <label className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="w-10">Pan</span>
                      <div className="flex-1 relative h-8 flex items-center">
                        <div className="absolute inset-x-0 h-1.5 rounded-full bg-strong" />
                        <div className="absolute h-1.5 rounded-full bg-foreground/30" style={{ left: `${Math.min(panPct, 50)}%`, width: `${Math.abs(panPct - 50)}%` }} />
                        <input
                          type="range"
                          min={-100}
                          max={100}
                          value={Math.round(panVal * 100)}
                          onChange={(e) => {
                            const pan = Number(e.target.value) / 100;
                            const volume = levels[selectedLaneId]?.volume ?? lane.volume;
                            applyLevel(selectedLaneId, { volume, pan });
                            player.setLaneLevel(selectedLaneId, { volume, pan });
                          }}
                          className="absolute inset-0 w-full h-8 opacity-0 cursor-pointer"
                          aria-label={`${cleanTrackName(lane.name)} pan`}
                        />
                        <div className="absolute w-6 h-6 rounded-full bg-gradient-to-b from-white to-[#b9b9c2] shadow pointer-events-none" style={{ left: `calc(${panPct}% - 12px)` }} />
                      </div>
                      <span className="font-mono text-[11px] w-14 text-right">{panLbl}</span>
                    </label>
                  </div>
                </div>
              );
            })()}

            {materialShelf}
          </div>
        )}
      </main>

      {/* ---- Mobile transport bar (bottom, in flow) ---- */}
      {/* On every view, Pads included: the tempo and the stop button are as
          much the pads' as the timeline's, and the bar that came and went with
          the tab moved the screen under the thumb. Undo and redo live up with
          the view tabs, which is what leaves room here for loop. */}
      <div className="shrink-0 h-[72px] short:h-14 px-3 pb-[var(--safe-b)] box-content bg-room border-t border-panelline flex items-center gap-2 wide:hidden">
        <button
          type="button"
          disabled={!playable}
          onClick={playback.transport.play}
          className="size-12 short:size-10 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center shrink-0"
          aria-label={playing ? "Stop" : "Play"}
        >
          {playing ? <Square className="size-5 fill-current" /> : <Play className="size-5" />}
        </button>
        <button type="button" onClick={stopAll} onDoubleClick={playback.transport.panic} className="size-9 rounded-xl bg-white/5 text-foreground/60 flex items-center justify-center shrink-0" aria-label="Stop" title="Stop the arrangement and previews; pads play on. Twice: everything, back to the start">
          <Square className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => player.setLoop(!player.loop)}
          aria-pressed={player.loop}
          className={cn("size-9 rounded-xl flex items-center justify-center shrink-0", player.loop ? "bg-white/10 text-primary" : "bg-white/5 text-foreground/60")}
          aria-label={player.loop ? "Disable loop" : "Enable loop"}
        >
          <Repeat className="size-4" />
        </button>
        {(view === "timeline" || rec.recording) && !listening && (
          <>
            <RecordButton rec={rec} className="size-9 rounded-xl bg-white/5" />
            <RecordSettings rec={rec} className="h-9 w-5 -ml-1.5" />
          </>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-mono text-lg leading-none">{position}</div>
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground mt-1">time</div>
        </div>
        <div className="text-right">
          <TempoReading
            tempo={tempo}
            onCommit={listening ? undefined : (bpm) => editor.propose([tempoIntent(room, bpm)])}
            className="text-right"
          />
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground mt-1">bpm</div>
        </div>
      </div>
      {downloads.dialog}
      {shortcutsOpen && (
        <ShortcutsHelp
          onClose={() => setShortcutsOpen(false)}
          groups={ROOM_SHORTCUTS}
          footer={ROOM_SHORTCUTS_FOOTER}
        />
      )}
      {guard.dialog}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pad grid: tracks as columns, timeline clips as rows
// ---------------------------------------------------------------------------

/**
 * Join and Leave, where a room you're in has Save. Join is the filled one:
 * it's the step listening in leads to. Leave is the page's back arrow,
 * spelled out — the person listening may not know the arrow is the way out.
 */
/**
 * Hands the stage's page one stable pair of functions (stop, ask). The page
 * never starts playback: the room plays when the person presses play. Reads the room
 * through refs, so a re-render doesn't hand over a new pair.
 */
function StageHandleBridge({
  stage,
  playing,
  stop,
  ask,
}: {
  stage: StageControls;
  playing: boolean;
  stop: () => void;
  ask: () => Promise<boolean>;
}) {
  const playingRef = useRef(playing);
  playingRef.current = playing;
  const stopRef = useRef(stop);
  stopRef.current = stop;
  const { onHandle } = stage;
  useEffect(() => {
    onHandle({
      stop: () => {
        if (playingRef.current) stopRef.current();
      },
      ask,
    });
  }, [onHandle, ask]);
  return null;
}

function ListenActions({ listen, compact = false }: { listen: ListenControls; compact?: boolean }) {
  const signedIn = useSession() !== null;
  return (
    <div className="flex shrink-0 items-center gap-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={listen.onLeave}
        className={cn("gap-1.5 text-muted-foreground hover:text-foreground", compact && "h-9 px-2.5")}
      >
        <LogOut className="size-4" />
        Leave
      </Button>
      <Button size="sm" onClick={listen.onJoin} className={cn("gap-1.5 font-bold", compact && "h-9 px-3")}>
        <Users className="size-4" />
        {signedIn ? (compact ? "Join" : "Join jam") : compact ? "Sign in" : "Sign in to join"}
      </Button>
    </div>
  );
}

/**
 * Listening in, said in words under the header: you can hear the room but
 * not touch it, and how to change that. Without it, a room where nothing
 * responds looks broken.
 */
function ListenBanner({ listen }: { listen: ListenControls }) {
  const signedIn = useSession() !== null;
  return (
    <div className="z-20 flex shrink-0 items-center gap-3 border-b border-primary/25 bg-primary/10 px-3 py-2 lg:px-4">
      <span className="relative flex size-2 shrink-0">
        <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-60" />
        <span className="relative inline-flex size-2 rounded-full bg-primary" />
      </span>
      <p className="min-w-0 flex-1 text-xs leading-snug sm:text-sm">
        <span className="font-semibold">You're listening in.</span>{" "}
        <span className="text-muted-foreground">
          {signedIn
            ? "Join the jam to edit and add tracks."
            : "To edit or add tracks, sign in and join the jam."}
        </span>
      </p>
      <Button size="sm" onClick={listen.onJoin} className="h-8 shrink-0 gap-1.5 px-3 font-bold">
        <Users className="size-4" />
        {signedIn ? "Join jam" : "Sign in to join"}
      </Button>
    </div>
  );
}

function PresenceBar({
  participants,
  status,
  isHost,
  listening = false,
}: {
  participants: JamParticipant[] | null;
  status: SocketStatus;
  isHost: boolean;
  listening?: boolean;
}) {
  const dot =
    status === "open" ? "bg-[var(--added)]" : status === "connecting" ? "bg-[var(--changed)]" : "bg-[var(--removed)]";
  const role = listening ? "listening" : isHost ? "host" : "guest";
  return (
    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
      <span className={cn("size-1.5 rounded-full", dot, status === "open" && "animate-pulse")} />
      <span>{role}</span>
      {participants && participants.length > 0 && (
        <>
          <span>·</span>
          <div className="flex -space-x-1">
            {participants.map((p) => (
              <Avatar key={participantKey(p)} name={p.username} url={null} className="size-4 ring-1 ring-background" />
            ))}
          </div>
          <span>{participants.length} in room</span>
        </>
      )}
    </div>
  );
}

type RoomView = "pads" | "timeline" | "mixer";

/** Each view's tab: its name, and what it is for (the tooltip's second line). */
const VIEW_LABELS: Record<RoomView, { label: string; hint: string }> = {
  timeline: { label: "Arrange", hint: "The song on a timeline" },
  pads: { label: "Pads", hint: "Launch clips live" },
  mixer: { label: "Mix", hint: "Levels, pans and mutes" },
};

const VIEW_ICONS: Record<string, ReactNode> = {
  Pads: <svg className="size-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><rect x={3} y={3} width={8} height={8} rx={2} /><rect x={13} y={3} width={8} height={8} rx={2} /><rect x={3} y={13} width={8} height={8} rx={2} /><rect x={13} y={13} width={8} height={8} rx={2} /></svg>,
  Arrange: <svg className="size-4" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" viewBox="0 0 24 24"><path d="M3 6h10M7 12h14M3 18h8" /></svg>,
  Mix: <svg className="size-4" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" viewBox="0 0 24 24"><path d="M6 4v16M12 4v16M18 4v16" /><rect x={4} y={13} width={4} height={3} rx={1} fill="currentColor" /><rect x={10} y={7} width={4} height={3} rx={1} fill="currentColor" /><rect x={16} y={15} width={4} height={3} rx={1} fill="currentColor" /></svg>,
};

/** A new seed's name, typed in the header where a seed's title would sit. */
function TitleField({ value, onChange, className }: { value: string; onChange: (v: string) => void; className?: string }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      // The page's shortcuts (Space plays) must not see typing.
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter" || e.key === "Escape") e.currentTarget.blur();
      }}
      placeholder="Untitled seed"
      aria-label="Seed name"
      maxLength={120}
      className={cn(
        "-mx-1 w-full min-w-0 rounded bg-transparent px-1 font-semibold outline-none placeholder:text-muted-foreground hover:bg-white/5 focus:bg-white/10",
        className,
      )}
    />
  );
}

/** A phone's fader: a range input under a drawn track and knob, so a thumb
 * gets 32px of target on a 6px line. `centred` fills from the middle (pan). */
function PhoneSlider({
  value,
  min = 0,
  onChange,
  label,
  centred = false,
}: {
  value: number;
  /** The low end; the high end is 1. */
  min?: number;
  onChange: (v: number) => void;
  label: string;
  centred?: boolean;
}) {
  const pct = Math.min(100, Math.max(0, ((value - min) / (1 - min)) * 100));
  return (
    <div className="relative flex h-8 min-w-0 flex-1 items-center">
      <div className="absolute inset-x-0 h-1.5 rounded-full bg-strong" />
      {centred ? (
        <div className="absolute h-1.5 rounded-full bg-foreground/30" style={{ left: `${Math.min(pct, 50)}%`, width: `${Math.abs(pct - 50)}%` }} />
      ) : (
        <div className="absolute left-0 h-1.5 rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg, var(--primary) 70%, #e9d86a)" }} />
      )}
      <input
        type="range"
        min={0}
        max={100}
        value={Math.round(pct)}
        onChange={(e) => onChange(min + (Number(e.target.value) / 100) * (1 - min))}
        className="absolute inset-0 h-8 w-full cursor-pointer opacity-0"
        aria-label={label}
      />
      {/* Offset by its own share of the travel, so it never hangs past either end. */}
      <div
        className="pointer-events-none absolute size-5 rounded-full bg-gradient-to-b from-white to-[#b9b9c2] shadow"
        style={{ left: `${pct}%`, transform: `translateX(-${pct}%)` }}
      />
    </div>
  );
}

function ViewSwitch({
  view,
  active,
  onClick,
  iconOnly = false,
}: {
  view: RoomView;
  active: boolean;
  onClick: () => void;
  /** The name only from 2xl up; the icon and the tooltip carry it below. */
  iconOnly?: boolean;
}) {
  const { label, hint } = VIEW_LABELS[view];
  const button = (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={label}
      className={cn(
        "h-8 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors",
        active ? "bg-controlline text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]" : "text-muted-foreground hover:text-foreground/80",
        iconOnly && "px-2.5 2xl:px-3",
      )}
    >
      {VIEW_ICONS[label]}
      <span className={cn(iconOnly && "hidden 2xl:inline")}>{label}</span>
    </button>
  );
  // A phone's tabs carry their names; only the icon row needs a tooltip.
  if (!iconOnly) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="bottom">
        <div className="font-semibold">{label}</div>
        <div className="text-muted-foreground">{hint}</div>
      </TooltipContent>
    </Tooltip>
  );
}

/** Build a same-origin URL for a public pool blob, given its audioKey.
 * Uses the core API's /api/audio/blob/:hash endpoint which streams public
 * blobs without auth. Works in dev (Vite proxies to core API on 8787) and
 * in production (forwarded by the account worker). */
function publicAudioUrl(audioKey: string): string | undefined {
  // Pool keys are "<prefix>blobs/<hash>.wav". Try both common prefixes.
  const hash = hashOfPoolKey(audioKey, "beta/") ?? hashOfPoolKey(audioKey, "");
  if (!hash) return undefined;
  return `/api/audio/blob/${encodeURIComponent(hash)}`;
}

let localIdCounter = 0;
function mintLocalId(kind: "track" | "clip"): string {
  return `hydrate_${kind[0]}_${++localIdCounter}`;
}

function LoadingScreen() {
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <Loader2 className="size-8 animate-spin text-muted-foreground" />
    </div>
  );
}

function ErrorScreen({ seedId }: { seedId: string }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 text-center text-sm text-muted-foreground">
      <p>Could not load seed.</p>
      <Button asChild variant="outline" size="sm">
        <Link to={seedPath(seedId)}>Back</Link>
      </Button>
    </div>
  );
}
